import {t, useLanguage} from '../i18n';
import LanguagePicker from '../i18n/LanguagePicker';
// src/pages/ProfilePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { signOut, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import fsClient from "../data/firestoreClient";
import {
  getCapacitorRuntimeDebug,
  httpRequestJson,
  isIosCapacitor,
} from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid, clearLoopAuthState } from "../utils/manualAuth";
import { firebaseConfigRuntime } from "../firebase";
import { registerPushNotifications } from "../notifications/registerPushNotifications";
import { isFirebaseNativeAvailable, signOut as nativeFirebaseSignOut } from "../native/firebaseNative";
import { getSafeAuthRedirectPath } from "../auth/authNavigation";
import { formatPhoneDisplay, normalizePhoneToE164, parsePhoneValue } from "../utils/phoneUtils";
import { PhoneInput } from "../components/PhoneKit";
import MyPaymentsPage from "./MyPaymentsPage";
import AccountAccessMethods from "../components/AccountAccessMethods";
import { LocationSearchInput } from "../NewBusinessPage";
import { readPushPermission } from "../notifications/registerPushNotifications";
import "./AccountRefresh.css";
import {
  cancelPhoneLinkVerification,
  confirmPhoneLinkVerification,
  startPhoneLinkVerification,
  syncCurrentVerifiedPhoneIdentity,
} from "../auth/phoneAuth";

const BRAND = "#00675f";
const BRAND_2 = "#00897f";

// Firebase SMS verification is the trusted phone identity source.

const PANAMA_PROVINCES = [
  "Bocas del Toro",
  "Chiriquí",
  "Coclé",
  "Colón",
  "Darién",
  "Herrera",
  "Los Santos",
  "Panamá",
  "Panamá Oeste",
  "Veraguas",
  "Guna Yala",
  "Emberá-Wounaan",
  "Ngäbe-Buglé",
];

const PANAMA_CITIES_BY_PROVINCE = {
  "Bocas del Toro": ["Bocas del Toro", "Changuinola", "Almirante", "Isla Colón", "Guabito", "Chiriquí Grande"],
  "Chiriquí": ["David", "Boquete", "Dolega", "Bugaba", "Volcán", "Puerto Armuelles", "Boquerón", "La Concepción"],
  "Coclé": ["Penonomé", "Aguadulce", "Antón", "Natá", "La Pintada", "El Valle de Antón"],
  "Colón": ["Colón", "Sabanitas", "Cativá", "Cristóbal", "Portobelo", "Nombre de Dios", "Buena Vista"],
  "Darién": ["La Palma", "Metetí", "Yaviza", "Santa Fe", "Garachiné"],
  "Herrera": ["Chitré", "Parita", "Pesé", "Ocú", "Las Minas", "Santa María"],
  "Los Santos": ["Las Tablas", "Pedasí", "Guararé", "Tonosí", "Macaracas", "Pocrí"],
  "Panamá": [
    "Panamá",
    "San Miguelito",
    "Costa del Este",
    "Punta Pacífica",
    "San Francisco",
    "Obarrio",
    "Bella Vista",
    "El Cangrejo",
    "Marbella",
    "Casco Antiguo",
    "Clayton",
    "Albrook",
    "Condado del Rey",
    "Brisas del Golf",
    "Juan Díaz",
    "Tocumen",
    "Bethania",
    "El Dorado",
    "Costa Sur",
    "Parque Lefevre",
  ],
  "Panamá Oeste": ["Arraiján", "La Chorrera", "Vista Alegre", "Nuevo Chorrillo", "Chame", "Coronado", "Capira", "San Carlos"],
  "Veraguas": ["Santiago", "Soná", "Atalaya", "Calobre", "Cañazas", "La Mesa", "Montijo", "Río de Jesús"],
  "Guna Yala": ["El Porvenir", "Cartí", "Narganá", "Ailigandí", "Ustupo"],
  "Emberá-Wounaan": ["Unión Chocó", "Cémaco", "Sambú", "Lajas Blancas"],
  "Ngäbe-Buglé": ["Llano Tugrí", "Kankintú", "Kusapín", "Besikó", "Müna", "Nole Duima"],
};

const PANAMA_CITY_OPTIONS = Array.from(
  new Set(Object.values(PANAMA_CITIES_BY_PROVINCE).flat())
);

function getProfileCityOptionsForProvince(province) {
  const cleanProvince = String(province || "").trim();
  if (!cleanProvince) return [];
  return PANAMA_CITIES_BY_PROVINCE[cleanProvince] || PANAMA_CITY_OPTIONS;
}

function cityBelongsToProvince(city, province) {
  const cleanCity = String(city || "").trim();
  if (!cleanCity) return true;
  const options = getProfileCityOptionsForProvince(province);
  return options.includes(cleanCity);
}

function normalizeEmailValue(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizePhoneValue(value) {
  return normalizePhoneToE164(value, "PA");
}

function profilePhoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function profileTimestampToLabel(value) {
  try {
    if (!value) return "";
    const d = typeof value.toDate === "function" ? value.toDate() : value.seconds ? new Date(Number(value.seconds) * 1000) : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-PA", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) {
    return "";
  }
}

const PHONE_OTP_RESEND_COOLDOWN_SECONDS = 60;
const PHONE_OTP_RATE_LIMIT_COOLDOWN_SECONDS = 30 * 60;
const PHONE_OTP_HARD_RATE_LIMIT_COOLDOWN_SECONDS = 6 * 60 * 60;
const PHONE_OTP_COOLDOWN_STORAGE_PREFIX = "loop_phone_otp_cooldown_r35";

function phoneErrorRaw(err) {
  return [
    err?.code,
    err?.nativeCode,
    err?.message,
    err?.details?.code,
    err?.details?.message,
    err,
  ]
    .filter(Boolean)
    .map((value) => String(value))
    .join(" ")
    .toLowerCase();
}

function isPhoneHardRateLimitError(err) {
  const raw = phoneErrorRaw(err);
  return (
    raw.includes("phone-rate-limited") ||
    raw.includes("auth/too-many-requests") ||
    raw.includes("error code:39") ||
    raw.includes("error code: 39") ||
    raw.includes("17499") ||
    raw.includes("blocked all requests") ||
    raw.includes("unusual activity") ||
    raw.includes("17010")
  );
}

function isPhoneRateLimitError(err) {
  const raw = phoneErrorRaw(err);
  return (
    isPhoneHardRateLimitError(err) ||
    raw.includes("too-many-requests") ||
    raw.includes("resource-exhausted") ||
    raw.includes("try again later")
  );
}

function phoneRateLimitCooldownSeconds(err) {
  return isPhoneHardRateLimitError(err)
    ? PHONE_OTP_HARD_RATE_LIMIT_COOLDOWN_SECONDS
    : PHONE_OTP_RATE_LIMIT_COOLDOWN_SECONDS;
}

function phoneErrorMessage(err) {
  const raw = phoneErrorRaw(err);
  if (isPhoneHardRateLimitError(err)) {
    return "Firebase bloqueó temporalmente el envío de SMS por sus controles de seguridad. No solicites otro código desde este dispositivo por varias horas.";
  }
  if (isPhoneRateLimitError(err)) {
    return "Se hicieron demasiados intentos de verificación. Espera antes de solicitar otro código.";
  }
  if (
    raw.includes("credential-already-in-use") ||
    raw.includes("phone-in-use") ||
    raw.includes("already-exists")
  ) {
    return "Este teléfono ya está vinculado a otra cuenta de Loop.";
  }
  if (raw.includes("provider-already-linked")) {
    return "Esta cuenta ya tiene un teléfono verificado. Para cambiarlo usaremos el flujo seguro de cambio de número.";
  }
  if (raw.includes("session-missing") || raw.includes("verification-id-missing")) {
    return "La verificación expiró. Pide un código nuevo.";
  }
  if (raw.includes("expired") || raw.includes("deadline") || raw.includes("timeout")) {
    return "El código o la solicitud expiró. Pide uno nuevo.";
  }
  if (raw.includes("invalid-verification-code") || raw.includes("invalid-code")) {
    return "Código inválido. Revisa los 6 dígitos e intenta nuevamente.";
  }
  if (raw.includes("invalid-phone") || raw.includes("invalid-phone-number")) {
    return "Revisa el número de teléfono y el código de país.";
  }
  if (
    raw.includes("phone-play-integrity-fallback") ||
    raw.includes("18002") ||
    raw.includes("not recognized by play store")
  ) {
    return "Esta instalación no fue reconocida por Google Play. Instala la versión de prueba desde Google Play y vuelve a intentarlo.";
  }
  if (raw.includes("invalid-app-credential")) {
    const hostname =
      typeof window !== "undefined"
        ? String(window.location?.hostname || "").trim().toLowerCase()
        : "";

    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "La verificación por SMS no está disponible desde el entorno local. Usa la app móvil o la versión web publicada.";
    }

    return "Firebase no pudo validar la solicitud de SMS. Intenta nuevamente desde la versión web publicada o desde la app móvil.";
  }
  if (raw.includes("captcha") || raw.includes("app-not-authorized")) {
    return "Firebase no pudo validar esta solicitud. En web, usa la versión publicada; en móvil, cierra y vuelve a abrir la aplicación.";
  }
  if (raw.includes("requires-recent-login")) {
    return "Por seguridad, cierra sesión e inicia nuevamente antes de verificar el teléfono.";
  }
  if (raw.includes("unauthenticated") || raw.includes("auth-token") || raw.includes("current-user-missing")) {
    return "Debes iniciar sesión nuevamente para verificar tu teléfono.";
  }
  return "No pudimos completar la verificación por SMS. Intenta nuevamente.";
}

function phoneOtpCooldownStorageKey(uid) {
  const cleanUid = String(uid || "").trim();
  return cleanUid ? `${PHONE_OTP_COOLDOWN_STORAGE_PREFIX}:${cleanUid}` : "";
}

function readPhoneOtpCooldownUntil(uid) {
  try {
    if (typeof localStorage === "undefined") return 0;
    const key = phoneOtpCooldownStorageKey(uid);
    if (!key) return 0;
    const value = Number(localStorage.getItem(key) || 0);
    return Number.isFinite(value) && value > Date.now() ? value : 0;
  } catch (_) {
    return 0;
  }
}

function writePhoneOtpCooldownUntil(uid, until) {
  try {
    if (typeof localStorage === "undefined") return;
    const key = phoneOtpCooldownStorageKey(uid);
    if (!key) return;
    if (Number(until) > Date.now()) {
      localStorage.setItem(key, String(Number(until)));
    } else {
      localStorage.removeItem(key);
    }
  } catch (_) {}
}

function formatPhoneOtpCooldown(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  if (total < 60) return `${total}s`;
  const minutes = Math.ceil(total / 60);
  return `${minutes} min`;
}

const DEFAULT_USER_NOTIFICATION_SETTINGS = {
  pushEnabled: false,
  emailEnabled: true,
  clientBookingUpdates: true,
  clientBookingReminders: true,
  clientReviewRequests: true,
  businessNotifications: true,
  newBookingRequests: true,
  workerNotifications: true,
  whatsappClientReminders: true,
  marketingEmails: false,
};

function normalizeUserNotificationSettings(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const normalized = { ...DEFAULT_USER_NOTIFICATION_SETTINGS };

  Object.keys(DEFAULT_USER_NOTIFICATION_SETTINGS).forEach((key) => {
    if (typeof source[key] === "boolean") normalized[key] = source[key];
  });

  // Legacy/backward compatibility aliases used by earlier notification phases.
  if (typeof source.clientNotifications === "boolean") {
    normalized.clientBookingUpdates = source.clientNotifications;
    normalized.clientBookingReminders = source.clientNotifications;
  }
  if (typeof source.bookingReminder1h === "boolean") {
    normalized.clientBookingReminders = source.bookingReminder1h;
  }
  if (typeof source.reviewRequests === "boolean") {
    normalized.clientReviewRequests = source.reviewRequests;
  }
  if (typeof source.businessAlerts === "boolean") {
    normalized.businessNotifications = source.businessAlerts;
  }
  if (typeof source.workerReminder2h === "boolean") {
    normalized.workerNotifications = source.workerReminder2h;
  }
  if (typeof source.receiveWhatsappReminders === "boolean") {
    normalized.whatsappClientReminders = source.receiveWhatsappReminders;
  }

  return normalized;
}

function notificationSnapshot(raw = {}) {
  const normalized = normalizeUserNotificationSettings(raw);
  return {
    ...normalized,
    // Keep older keys in sync so existing Cloud Functions/settings checks continue working.
    clientNotifications: normalized.clientBookingUpdates,
    bookingReminder1h: normalized.clientBookingReminders,
    reviewRequests: normalized.clientReviewRequests,
    receiveWhatsappReminders: normalized.whatsappClientReminders,
    whatsappOptOut: normalized.whatsappClientReminders === false,
    whatsappOptIn: normalized.whatsappClientReminders !== false,
  };
}


const PROFILE_FAST_CACHE_PREFIX = "loop_profile_fast_cache_v1";

function profileFastCacheKey(uid) {
  const cleanUid = String(uid || "").trim();
  return cleanUid ? `${PROFILE_FAST_CACHE_PREFIX}:${cleanUid}` : "";
}

function readProfileFastCache(uid) {
  try {
    if (typeof localStorage === "undefined") return null;
    const key = profileFastCacheKey(uid);
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeProfileFastCache(uid, data) {
  try {
    if (typeof localStorage === "undefined") return;
    const key = profileFastCacheKey(uid);
    if (!key) return;
    localStorage.setItem(key, JSON.stringify({ ...(data || {}), cachedAt: Date.now() }));
  } catch (_) {}
}

function withProfileTimeout(promise, ms = 2800) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("profile-load-timeout")), ms);
    }),
  ]).finally(() => {
    try { if (timer) clearTimeout(timer); } catch (_) {}
  });
}



function getProviderIds(auth) {
  try {
    const current = auth && auth.currentUser ? auth.currentUser : null;
    if (!current || !Array.isArray(current.providerData)) return [];
    return current.providerData.map((p) => p && p.providerId).filter(Boolean);
  } catch (_) {
    return [];
  }
}

function canPasswordReauthenticate(auth) {
  return getProviderIds(auth).includes("password");
}

async function reauthenticateForAccountDelete({ auth, password }) {
  const current = auth && auth.currentUser ? auth.currentUser : null;
  if (!current) return { ok: false, reason: "no-current-user" };

  const providers = getProviderIds(auth);
  if (!providers.includes("password")) {
    return { ok: true, reason: "non-password-provider" };
  }

  const email = current.email || "";
  if (!email || !password) {
    return { ok: false, reason: "missing-password" };
  }

  const credential = EmailAuthProvider.credential(email, password);
  await reauthenticateWithCredential(current, credential);
  return { ok: true, reason: "password-reauthenticated" };
}


function getDeleteAccountEndpoint() {
  const fromEnv = typeof process !== "undefined" && process.env ? process.env.REACT_APP_DELETE_ACCOUNT_URL : "";
  if (fromEnv) return fromEnv;
  const projectId = firebaseConfigRuntime && firebaseConfigRuntime.projectId ? firebaseConfigRuntime.projectId : "web-app-d9799";
  return `https://us-central1-${projectId}.cloudfunctions.net/deleteMyAccount`;
}

async function getDeleteAccountIdToken(auth) {
  try {
    const current = auth && auth.currentUser ? auth.currentUser : null;
    if (current && typeof current.getIdToken === "function") {
      const token = await current.getIdToken(true);
      if (token) return token;
    }
  } catch (_) {}

  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma) && ma.idToken) return ma.idToken;
  } catch (_) {}

  return null;
}

function clearLocalAuthAfterDelete() {
  clearLoopAuthState();
}

function getEffectiveUser(user, auth) {
  if (user && user.uid) return { uid: user.uid, email: user.email || "", displayName: user.displayName || "" };

  try {
    const current = auth && auth.currentUser ? auth.currentUser : null;
    if (current && current.uid) {
      return {
        uid: current.uid,
        email: current.email || "",
            displayName: current.displayName || "",
      };
    }
  } catch (_) {}

  try {
    if (isIosCapacitor()) {
      const ma = getManualAuth();
      if (ma && isManualAuthLikelyValid(ma) && ma.uid) {
        return {
          uid: ma.uid,
          email: ma.email || "",
                displayName: ma.displayName || "",
        };
      }
    }
  } catch (_) {}

  return null;
}

function normalizeProfileData(raw, effectiveUser, user) {
  const d = raw || {};
  return {
    displayName: d.nombre || d.displayName || effectiveUser?.displayName || user?.displayName || "",
    apellido: d.apellido || d.lastName || "",
    email: effectiveUser?.email || d.authEmail || d.email || "",
    phone: d.phoneDisplay || d.phone || d.phoneNormalized || d.telefonoNormalized || d.telefono || d.mobile || "",
    phoneE164: d.phoneE164 || d.phoneNormalized || normalizePhoneValue(d.phone || d.telefono || d.mobile || ""),
    phoneDisplay: d.phoneDisplay || formatPhoneDisplay(d.phoneE164 || d.phoneNormalized || d.phone || d.telefono || d.mobile || "", "PA"),
    phoneCountryCode: d.phoneCountryCode || parsePhoneValue(d.phoneE164 || d.phoneNormalized || d.phone || "", { defaultCountry: "PA" }).countryCode || "PA",
    phoneDialCode: d.phoneDialCode || parsePhoneValue(d.phoneE164 || d.phoneNormalized || d.phone || "", { defaultCountry: "PA" }).dialCode || "+507",
    phoneNational: d.phoneNational || parsePhoneValue(d.phoneE164 || d.phoneNormalized || d.phone || "", { defaultCountry: "PA" }).nationalNumber || "",
    phoneVerified: d.phoneVerified === true,
    phoneVerifiedAt: d.phoneVerifiedAt || null,
    phoneVerificationProvider: d.phoneVerificationProvider || "",
    phoneVerifiedE164: d.phoneVerifiedE164 || (d.phoneVerified === true ? (d.phoneE164 || d.phoneNormalized || normalizePhoneValue(d.phone || d.telefono || d.mobile || "")) : ""),
    address: d.address || d.direccion || "",
    city: d.city || "",
    province: d.province || "",
    postalCode: d.postalCode || d.zip || "",
    birthDate: d.birthDate || "",
    gender: d.gender || "",
    preferredContactChannel: d.preferredContactChannel || "email",
    preferredReminderTiming: d.preferredReminderTiming || "24h_2h",
    defaultBookingLocation: d.defaultBookingLocation || "",
    favoriteZone: d.favoriteZone || "",
    bookingNotes: d.bookingNotes || "",
    privacyPersonalization: d.privacyPersonalization !== false,
    receiveWhatsappReminders: Boolean(d.receiveWhatsappReminders),
  };
}

function buildProfileSavePayload(data, effectiveUser, useNativeSafeTimestamp) {
  const now = new Date();
  const parsedPhone = parsePhoneValue(data.phone || data.phoneE164 || "", { defaultCountry: "PA" });
  const nextPhoneE164 = parsedPhone.e164 || "";
  return {
    nombre: data.displayName ? data.displayName.trim() : null,
    displayName: data.displayName ? data.displayName.trim() : null,
    apellido: data.apellido ? data.apellido.trim() : null,
    lastName: data.apellido ? data.apellido.trim() : null,
    phone: parsedPhone.display || (data.phone ? data.phone.trim() : null),
    telefono: parsedPhone.display || (data.phone ? data.phone.trim() : null),
    phoneDisplay: parsedPhone.display || null,
    phoneE164: nextPhoneE164 || null,
    phoneNormalized: nextPhoneE164 || null,
    telefonoNormalized: nextPhoneE164 || null,
    phoneCountryCode: parsedPhone.countryCode || null,
    phoneDialCode: parsedPhone.dialCode || null,
    phoneNational: parsedPhone.nationalNumber || null,
    // phoneVerified* fields are intentionally omitted. Firestore Rules R32
    // reserve them for the Admin SDK after Firebase Auth verifies the SMS.
    address: null,
    direccion: null,
    city: data.city ? data.city.trim() : null,
    province: data.province ? data.province.trim() : null,
    postalCode: null,
    birthDate: data.birthDate || null,
    gender: data.gender || null,
    preferredContactChannel: data.preferredContactChannel || "email",
    preferredReminderTiming: data.preferredReminderTiming || "24h_2h",
    defaultBookingLocation: data.defaultBookingLocation || null,
    favoriteZone: data.favoriteZone ? data.favoriteZone.trim() : null,
    bookingNotes: data.bookingNotes ? data.bookingNotes.trim() : null,
    privacyPersonalization: data.privacyPersonalization !== false,
    receiveWhatsappReminders: Boolean(data.receiveWhatsappReminders),
    whatsappOptIn: Boolean(data.receiveWhatsappReminders),
    whatsappOptOut: Boolean(data.receiveWhatsappReminders) === false,
    updatedAt: useNativeSafeTimestamp ? now.toISOString() : serverTimestamp(),
    updatedAtMs: now.getTime(),
  };
}

async function saveProfileDocSafe({ db, uid, payload }) {
  const path = `users/${uid}`;

  if (isIosCapacitor()) {
    // iOS/Capacitor uses native HTTP/REST and must never leave the UI
    // frozen forever if Firestore rejects or WKWebView stalls.
    if (typeof fsClient.setDocByPath === "function") {
      await withProfileTimeout(
        fsClient.setDocByPath(path, payload, { db, merge: true }),
        10000
      );
      return;
    }

    try {
      await withProfileTimeout(
        fsClient.updateDocByPath(path, payload, { db }),
        10000
      );
      return;
    } catch (updateError) {
      if (typeof fsClient.setDocByPath === "function") {
        await withProfileTimeout(
          fsClient.setDocByPath(path, payload, { db, merge: true }),
          10000
        );
        return;
      }
      throw updateError;
    }
  }

  const uref = doc(db, "users", uid);
  try {
    await updateDoc(uref, payload);
  } catch (_) {
    await setDoc(uref, payload, { merge: true });
  }
}

function initialsFromName(name, email) {
  const cleanName = String(name || "").trim();
  if (cleanName) {
    const parts = cleanName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }

  const cleanEmail = String(email || "").trim();
  if (cleanEmail) return cleanEmail.slice(0, 2).toUpperCase();

  return "U";
}

function completenessScore(data) {
  const fields = ["displayName", "apellido", "email", "phone", "city", "province", "birthDate"];
  const completed = fields.filter((key) => String(data?.[key] || "").trim()).length;
  return Math.round((completed / fields.length) * 100);
}

function profileSnapshot(data) {
  const safe = data || {};
  return {
    displayName: String(safe.displayName || ""),
    apellido: String(safe.apellido || ""),
    email: String(safe.email || ""),
    phone: String(safe.phone || ""),
    phoneE164: normalizePhoneValue(safe.phone || safe.phoneE164 || ""),
    phoneNormalized: normalizePhoneValue(safe.phone || safe.phoneE164 || ""),
    phoneVerified: Boolean(safe.phoneVerified),
    phoneVerifiedE164: normalizePhoneValue(safe.phoneVerifiedE164 || ""),
    city: String(safe.city || ""),
    province: String(safe.province || ""),
    birthDate: String(safe.birthDate || ""),
    gender: String(safe.gender || ""),
    preferredContactChannel: String(safe.preferredContactChannel || "email"),
    preferredReminderTiming: String(safe.preferredReminderTiming || "24h_2h"),
    defaultBookingLocation: String(safe.defaultBookingLocation || ""),
    favoriteZone: String(safe.favoriteZone || ""),
    bookingNotes: String(safe.bookingNotes || ""),
    privacyPersonalization: safe.privacyPersonalization !== false,
    receiveWhatsappReminders: Boolean(safe.receiveWhatsappReminders),
  };
}

function sameProfileSnapshot(a, b) {
  try {
    return JSON.stringify(profileSnapshot(a)) === JSON.stringify(profileSnapshot(b));
  } catch (_) {
    return false;
  }
}

function mergeVerifiedPhoneIntoSavedSnapshot(previousSnapshot, nextData) {
  const previous = profileSnapshot(previousSnapshot || {});
  const next = profileSnapshot(nextData || {});
  return {
    ...previous,
    phone: next.phone,
    phoneE164: next.phoneE164,
    phoneNormalized: next.phoneNormalized,
    phoneVerified: true,
    phoneVerifiedE164: next.phoneVerifiedE164,
  };
}

const PROFILE_PANEL_META = {
  profile: {
    title: "Datos personales",
    description: "Administra tu información personal y datos de contacto principales.",
  },
  preferences: {
    title: "Notificaciones",
    description: "Elige tus avisos y cómo recibirlos.",
  },
  payments: {
title: "Mis pagos",
description: "Consulta tus pagos, reservas y renovaciones registradas en Loop.",
},
  security: {
    title: "Acceso y seguridad",
    description: "Gestiona tu acceso y tu cuenta.",
  },
};

function normalizeProfilePanel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["security", "account", "cuenta", "settings", "seguridad"].includes(raw)) return "security";
  if (["payments", "payment", "pagos", "historial", "transactions", "transacciones", "memberships", "membership", "membresias", "membres\u00edas", "planes", "renewals", "renovaciones"].includes(raw)) return "payments";
  if (["notifications", "notification", "notificaciones", "avisos", "preferences", "preferencias", "defaults", "reservas"].includes(raw)) return "preferences";
  return "profile";
}

const PHONE_PROFILE_HANDOFF_KEY = "booking_phone_profile_handoff_v1";
const PHONE_PROFILE_HANDOFF_MAX_AGE_MS = 15 * 60 * 1000;

function clearPhoneProfileHandoff() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.removeItem(PHONE_PROFILE_HANDOFF_KEY);
    }
  } catch (_) {}
}

function readPhoneProfileHandoff(uid) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return null;
    }

    const raw = window.sessionStorage.getItem(
      PHONE_PROFILE_HANDOFF_KEY
    );

    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const expectedUid = String(uid || "").trim();
    const handoffUid = String(parsed?.uid || "").trim();
    const phone = String(parsed?.phone || "").trim();
    const createdAt = Number(parsed?.createdAt || 0);
    const ageMs = Date.now() - createdAt;

    const isFresh =
      Number.isFinite(createdAt) &&
      createdAt > 0 &&
      ageMs >= -60000 &&
      ageMs <= PHONE_PROFILE_HANDOFF_MAX_AGE_MS;

    if (
      !expectedUid ||
      handoffUid !== expectedUid ||
      !phone ||
      !isFresh
    ) {
      clearPhoneProfileHandoff();
      return null;
    }

    return {
      uid: handoffUid,
      phone,
      createdAt,
    };
  } catch (_) {
    clearPhoneProfileHandoff();
    return null;
  }
}

function applyPhoneProfileHandoff(
  profile,
  handoff,
  effectiveUser
) {
  if (!handoff) return profile;

  const safeProfile = profile || {};
  const authVerifiedPhone = normalizePhoneValue(
    effectiveUser?.phoneNumber || ""
  );
  const profilePhone = normalizePhoneValue(
    safeProfile.phone || safeProfile.phoneE164 || ""
  );
  const verifiedPhone = normalizePhoneValue(
    safeProfile.phoneVerifiedE164 || ""
  );
  const verificationProvider = String(
    safeProfile.phoneVerificationProvider || ""
  ).trim().toLowerCase();

  const hasTrustedVerifiedPhone = Boolean(
    authVerifiedPhone ||
    (
      safeProfile.phoneVerified === true &&
      verificationProvider === "firebase_auth" &&
      profilePhone &&
      verifiedPhone &&
      profilePhone === verifiedPhone
    )
  );

  if (hasTrustedVerifiedPhone) {
    return safeProfile;
  }

  const parsedPhone = parsePhoneValue(
    handoff.phone,
    { defaultCountry: "PA" }
  );
  const phoneE164 =
    parsedPhone.e164 ||
    normalizePhoneValue(handoff.phone);

  if (!phoneE164) {
    return safeProfile;
  }

  const phoneDisplay =
    parsedPhone.display ||
    formatPhoneDisplay(phoneE164, "PA") ||
    handoff.phone;

  return {
    ...safeProfile,
    phone: phoneDisplay,
    phoneE164,
    phoneDisplay,
    phoneCountryCode:
      parsedPhone.countryCode ||
      safeProfile.phoneCountryCode ||
      "PA",
    phoneDialCode:
      parsedPhone.dialCode ||
      safeProfile.phoneDialCode ||
      "+507",
    phoneNational: parsedPhone.nationalNumber || "",
    phoneVerified: false,
    phoneVerifiedAt: null,
    phoneVerificationProvider: "",
    phoneVerifiedE164: "",
  };
}

function getInitialProfilePanel() {
  try {
    if (typeof window === "undefined") return "profile";
    const params = new URLSearchParams(window.location.search || "");
    return normalizeProfilePanel(params.get("section") || params.get("tab") || "");
  } catch (_) {}
  return "profile";
}

function syncProfilePanelToUrl(panel) {
  try {
    if (typeof window === "undefined" || !window.history || !window.location) return;
    const url = new URL(window.location.href);
    const cleanPanel = normalizeProfilePanel(panel);
    if (cleanPanel === "profile") url.searchParams.delete("section");
    else url.searchParams.set("section", cleanPanel);
    window.history.replaceState(window.history.state || {}, "", `${url.pathname}${url.search}${url.hash}`);
  } catch (_) {}
}

function FieldIcon({ type }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.1,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (type === "mail") {
    return <svg {...common}><path d="M4 6h16v12H4z" /><path d="m4 7 8 6 8-6" /></svg>;
  }

  if (type === "phone") {
    return <svg {...common}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.3 19.3 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.4 2.1L8 9.6a16 16 0 0 0 6.4 6.4l1.3-1.3a2 2 0 0 1 2.1-.4c.8.3 1.6.5 2.5.6a2 2 0 0 1 1.7 2Z" /></svg>;
  }

  if (type === "location") {
    return <svg {...common}><path d="M12 21s7-5.1 7-12a7 7 0 1 0-14 0c0 6.9 7 12 7 12Z" /><circle cx="12" cy="9" r="2.4" /></svg>;
  }

  if (type === "calendar") {
    return <svg {...common}><path d="M8 2v4" /><path d="M16 2v4" /><path d="M3.5 9.5h17" /><path d="M5.5 5h13A2.5 2.5 0 0 1 21 7.5v11A2.5 2.5 0 0 1 18.5 21h-13A2.5 2.5 0 0 1 3 18.5v-11A2.5 2.5 0 0 1 5.5 5Z" /></svg>;
  }

  if (type === "shield") {
    return <svg {...common}><path d="M12 3l8 4v6c0 5-3.5 7.5-8 8-4.5-.5-8-3-8-8V7l8-4Z" /><path d="M9 12l2 2 4-4" /></svg>;
  }

  return <svg {...common}><path d="M20 21a8 8 0 1 0-16 0" /><circle cx="12" cy="7" r="4" /></svg>;
}

function SectionCard({ eyebrow, title, description, children }) {
  return (
    <section className="profile-card">
      <div className="profile-section-head">
        <div>
          {eyebrow ? <div className="profile-eyebrow">{eyebrow}</div> : null}
          <h3>{title}</h3>
          {description ? <p>{description}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, type = "text", icon = "user", readOnly = false, placeholder = "", autoComplete }) {
  return (
    <label className="profile-field">
      <span className="profile-field-label">{label}</span>
      <span className={`profile-input-wrap ${readOnly ? "is-readonly" : ""}`}>
        <span className="profile-input-icon"><FieldIcon type={icon} /></span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} placeholder={placeholder} autoComplete={autoComplete} />
      </span>
    </label>
  );
}

function PhoneField({ label, value, onChange }) {
  return (
    <div className="profile-field profile-phonekit-field">
      <PhoneInput
        label={label}
        value={value || ""}
        onChange={(display, parsed) => {
          onChange(parsed?.display || display || "");
        }}
        defaultCountry="PA"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, icon = "user", children }) {
  const selectRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuLayout, setMenuLayout] = useState({
    mode: "dropdown",
    placement: "down",
    style: {},
  });

  const options = useMemo(() => {
    return React.Children.toArray(children)
      .filter((child) => React.isValidElement(child))
      .map((child) => {
        const rawLabel = child.props.children;
        const labelText = Array.isArray(rawLabel) ? rawLabel.join("") : String(rawLabel || "");
        const rawValue = child.props.value;
        return {
          value: rawValue === undefined || rawValue === null ? labelText : String(rawValue),
          label: labelText,
          disabled: child.props.disabled === true,
        };
      });
  }, [children]);

  const selectedValue = String(value || "");
  const selectedOption =
    options.find((option) => String(option.value) === selectedValue) ||
    options.find((option) => option.value === "") ||
    options[0] ||
    { value: "", label: "Selecciona" };

  const updateMenuLayout = () => {
    if (typeof window === "undefined" || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const isSheet = viewportWidth <= 720;

    if (isSheet) {
      setMenuLayout({ mode: "sheet", placement: "sheet", style: {} });
      return;
    }

    const safeMargin = 12;
    const gap = 8;
    const desiredHeight = options.length > 9 ? 320 : Math.max(180, options.length * 44 + 20);
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom - safeMargin);
    const spaceAbove = Math.max(0, rect.top - safeMargin);
    const placement = spaceBelow >= Math.min(desiredHeight, 260) || spaceBelow >= spaceAbove ? "down" : "up";
    const available = placement === "down" ? spaceBelow - gap : spaceAbove - gap;
    const maxHeight = Math.max(156, Math.min(320, available || 220));
    const minWidth = Math.max(rect.width, 220);
    const width = Math.min(minWidth, viewportWidth - safeMargin * 2);
    const left = Math.max(safeMargin, Math.min(rect.left, viewportWidth - width - safeMargin));

    const style = {
      left: `${Math.round(left)}px`,
      width: `${Math.round(width)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
    };

    if (placement === "up") {
      style.bottom = `${Math.round(viewportHeight - rect.top + gap)}px`;
    } else {
      style.top = `${Math.round(rect.bottom + gap)}px`;
    }

    setMenuLayout({ mode: "dropdown", placement, style });
  };

  useEffect(() => {
    if (!open) return undefined;

    updateMenuLayout();

    const closeFromOutside = (event) => {
      const target = event.target;
      if (selectRef.current && selectRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };

    const closeFromKeyboard = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    const reposition = () => updateMenuLayout();

    document.addEventListener("mousedown", closeFromOutside);
    document.addEventListener("touchstart", closeFromOutside);
    document.addEventListener("keydown", closeFromKeyboard);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      document.removeEventListener("mousedown", closeFromOutside);
      document.removeEventListener("touchstart", closeFromOutside);
      document.removeEventListener("keydown", closeFromKeyboard);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, options.length]);

  const openMenu = () => {
    if (!open) updateMenuLayout();
    setOpen((current) => !current);
  };

  const handleSelect = (option) => {
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  const menuOptions = (
    <>
      {options.map((option) => {
        const isActive = String(option.value) === selectedValue;
        return (
          <button
            key={`${option.value}-${option.label}`}
            type="button"
            className={isActive ? "is-active" : ""}
            onClick={() => handleSelect(option)}
            disabled={option.disabled}
            role="option"
            aria-selected={isActive}
          >
            <span>{option.label}</span>
            {isActive ? <strong aria-hidden="true">✓</strong> : null}
          </button>
        );
      })}
    </>
  );

  const dropdownMenu = open && typeof document !== "undefined"
    ? createPortal(
        menuLayout.mode === "sheet" ? (
          <div className="profile-select-sheet-layer" role="presentation">
            <button type="button" className="profile-select-sheet-backdrop" aria-label={t("Cerrar opciones")} onClick={() => setOpen(false)} />
            <div className="profile-select-sheet" ref={menuRef} role="listbox" aria-label={label}>
              <div className="profile-select-sheet-head">
                <div>
                  <span>{t("Seleccionar")}</span>
                  <strong>{label}</strong>
                </div>
                <button type="button" onClick={() => setOpen(false)} aria-label={t("Cerrar")}>×</button>
              </div>
              <div className="profile-select-sheet-options">{menuOptions}</div>
            </div>
          </div>
        ) : (
          <div
            className={`profile-select-menu is-${menuLayout.placement}`}
            role="listbox"
            aria-label={label}
            ref={menuRef}
            style={menuLayout.style}
          >
            {menuOptions}
          </div>
        ),
        document.body
      )
    : null;

  return (
    <div className="profile-field profile-select-field">
      <span className="profile-field-label">{label}</span>
      <div className={`profile-select-wrap ${open ? "is-open" : ""}`} ref={selectRef}>
        <button
          type="button"
          className="profile-select-button"
          ref={buttonRef}
          onClick={openMenu}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="profile-input-icon"><FieldIcon type={icon} /></span>
          <span className={`profile-select-value ${!selectedValue ? "is-placeholder" : ""}`}>
            {selectedOption.label || "Selecciona"}
          </span>
          <span className="profile-select-chevron" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
        {dropdownMenu}
      </div>
    </div>
  );
}

function TextAreaField({ label, value, onChange, icon = "location", placeholder = "" }) {
  return (
    <label className="profile-field profile-field-wide">
      <span className="profile-field-label">{label}</span>
      <span className="profile-input-wrap is-textarea">
        <span className="profile-input-icon"><FieldIcon type={icon} /></span>
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} />
      </span>
    </label>
  );
}

function NiceSwitch({ checked, onChange, label = "Cambiar preferencia" }) {
  return (
    <button type="button" className={`profile-switch ${checked ? "is-on" : ""}`} onClick={() => onChange(!checked)} aria-pressed={checked} aria-label={label}>
      <span />
    </button>
  );
}

function NotificationPreferenceRow({ title, copy, checked, onChange }) {
  return (
    <div className="profile-preference clean profile-notification-row">
      <div>
        <strong>{title}</strong>
        <span>{copy}</span>
      </div>
      <NiceSwitch checked={checked} onChange={onChange} label={title} />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="profile-page">
      <style>{profileStyles}</style>
      <div className="profile-shell">
        <div className="profile-skeleton hero" />
        <div className="profile-grid"><div className="profile-skeleton card" /><div className="profile-skeleton card" /></div>
      </div>
    </div>
  );
}

export default function ProfilePage({ db, auth, user, onClose }) {
  useLanguage();
  const effectiveUser = useMemo(() => getEffectiveUser(user, auth), [user, auth]);
  const shouldAskDeletePassword = useMemo(() => canPasswordReauthenticate(auth), [auth]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteAck, setDeleteAck] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [activePanel, setActivePanel] = useState(() => getInitialProfilePanel());
  const [mobileMenu, setMobileMenu] = useState(() => typeof window === "undefined" || !(new URLSearchParams(window.location.search).has("section") || new URLSearchParams(window.location.search).has("tab")));
  const [pendingExit, setPendingExit] = useState(false);
  const exitDialogRef = useRef(null);
  useEffect(() => {
    if (!pendingExit) return;
    const previous = document.activeElement;
    const buttons = () => Array.from(exitDialogRef.current?.querySelectorAll("button") || []);
    buttons()[0]?.focus();
    const keyboard = event => {
      if (event.key === "Escape") setPendingExit(false);
      if (event.key === "Tab") { const items = buttons(); if (!items.length) return; const next = event.shiftKey ? items[items.length-1] : items[0]; if ((event.shiftKey && document.activeElement === items[0]) || (!event.shiftKey && document.activeElement === items[items.length-1])) { event.preventDefault(); next.focus(); } }
    };
    window.addEventListener("keydown",keyboard);
    return () => { window.removeEventListener("keydown",keyboard); previous?.focus?.(); };
  }, [pendingExit]);
  const [devicePermission, setDevicePermission] = useState("unknown");
  const [signingOut, setSigningOut] = useState(false);
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [data, setData] = useState({
    displayName: "",
    apellido: "",
    email: "",
    phone: "",
    phoneE164: "",
    phoneDisplay: "",
    phoneCountryCode: "PA",
    phoneDialCode: "+507",
    phoneNational: "",
    phoneVerified: false,
    phoneVerifiedAt: null,
    phoneVerificationProvider: "",
    phoneVerifiedE164: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
    birthDate: "",
    gender: "",
    preferredContactChannel: "email",
    preferredReminderTiming: "24h_2h",
    defaultBookingLocation: "",
    favoriteZone: "",
    bookingNotes: "",
    privacyPersonalization: true,
    receiveWhatsappReminders: false,
  });

  const [notificationSettings, setNotificationSettings] = useState(() => normalizeUserNotificationSettings());
  const [lastSavedNotificationSettings, setLastSavedNotificationSettings] = useState(() => normalizeUserNotificationSettings());
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState("");
  const [pushRegistering, setPushRegistering] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpCode, setPhoneOtpCode] = useState("");
  const [phoneOtpSending, setPhoneOtpSending] = useState(false);
  const [phoneOtpVerifying, setPhoneOtpVerifying] = useState(false);
  const [phoneOtpStatus, setPhoneOtpStatus] = useState({ type: "", message: "" });
  const [phoneOtpCooldownUntil, setPhoneOtpCooldownUntil] = useState(0);
  const [phoneOtpCooldownSeconds, setPhoneOtpCooldownSeconds] = useState(0);
  const phoneRequestInFlightRef = useRef(false);

  const score = useMemo(() => completenessScore(data), [data]);
  const fullName = `${data.displayName || ""} ${data.apellido || ""}`.trim();
  const initials = initialsFromName(fullName, data.email);
  const hasUnsavedChanges = useMemo(() => {
    if (!lastSavedSnapshot) return false;
    return !sameProfileSnapshot(data, lastSavedSnapshot);
  }, [data, lastSavedSnapshot]);
  const hasUnsavedNotificationChanges = useMemo(() => {
    try {
      return JSON.stringify(notificationSnapshot(notificationSettings)) !== JSON.stringify(notificationSnapshot(lastSavedNotificationSettings));
    } catch (_) {
      return false;
    }
  }, [notificationSettings, lastSavedNotificationSettings]);
  const hasUnsavedPreferenceChanges = hasUnsavedChanges || hasUnsavedNotificationChanges;
  const lastSavedLabel = useMemo(() => {
    if (!lastSavedAt) return "";
    try {
      return new Date(lastSavedAt).toLocaleTimeString("es-PA", { hour: "2-digit", minute: "2-digit" });
    } catch (_) {
      return "";
    }
  }, [lastSavedAt]);

  const provinceCityOptions = useMemo(
    () => getProfileCityOptionsForProvince(data.province),
    [data.province]
  );

  const profilePhoneE164 = useMemo(() => normalizePhoneValue(data.phone || data.phoneE164 || ""), [data.phone, data.phoneE164]);
  const profilePhoneDisplay = useMemo(() => formatPhoneDisplay(profilePhoneE164 || data.phone || "", "PA"), [profilePhoneE164, data.phone]);
  const phoneIsVerified = useMemo(() => {
    const verified = normalizePhoneValue(data.phoneVerifiedE164 || "");
    const provider = String(data.phoneVerificationProvider || "").trim().toLowerCase();
    return Boolean(
      data.phoneVerified === true &&
      provider === "firebase_auth" &&
      profilePhoneE164 &&
      verified &&
      profilePhoneE164 === verified
    );
  }, [
    data.phoneVerified,
    data.phoneVerifiedE164,
    data.phoneVerificationProvider,
    profilePhoneE164,
  ]);
  const lastSavedPhoneE164 = useMemo(
    () => normalizePhoneValue(lastSavedSnapshot?.phoneE164 || lastSavedSnapshot?.phone || ""),
    [lastSavedSnapshot],
  );
  const phoneChangedSinceLastSave = useMemo(
    () => Boolean(lastSavedSnapshot && profilePhoneE164 !== lastSavedPhoneE164),
    [lastSavedSnapshot, profilePhoneE164, lastSavedPhoneE164],
  );
  const phoneVerifiedLabel = useMemo(() => profileTimestampToLabel(data.phoneVerifiedAt), [data.phoneVerifiedAt]);
  const phoneOtpCoolingDown = phoneOtpCooldownSeconds > 0;
  const canRequestPhoneOtp = Boolean(
    profilePhoneE164 &&
    profilePhoneDigits(profilePhoneE164).length >= 8 &&
    !phoneOtpCoolingDown
  );
  const phoneVerificationMode = phoneIsVerified ? "verified" : phoneOtpSent ? "code" : "ready";
  const phoneVerificationTitle = phoneIsVerified
    ? "Teléfono verificado"
    : phoneOtpSent
      ? "Ingresa el código SMS"
      : "Verifica tu teléfono";
  const phoneVerificationDescription = phoneIsVerified
    ? `${profilePhoneDisplay || "Tu teléfono"}${phoneVerifiedLabel ? ` · verificado ${phoneVerifiedLabel}` : ""}`
    : phoneOtpSent
      ? `Enviamos un código de 6 dígitos a ${profilePhoneDisplay || "tu teléfono"}.`
      : "Te enviaremos un código por SMS para confirmar tu número.";

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!effectiveUser || !effectiveUser.uid) {
        if (mounted) setLoading(false);
        return;
      }

      const fallbackProfile = normalizeProfileData(null, effectiveUser, user);
      const phoneProfileHandoff = readPhoneProfileHandoff(
        effectiveUser.uid
      );

      if (isIosCapacitor()) {
        const cached = readProfileFastCache(effectiveUser.uid);
        const initialProfile = cached ? normalizeProfileData(cached, effectiveUser, user) : fallbackProfile;
        const initialProfileWithHandoff =
          applyPhoneProfileHandoff(
            initialProfile,
            phoneProfileHandoff,
            effectiveUser
          );

        if (mounted) {
          setData(initialProfileWithHandoff);
          setLastSavedSnapshot(profileSnapshot(initialProfile));
          setLoading(false);
        }

        try {
          const [profileDoc, notificationDoc] = await Promise.all([
            withProfileTimeout(fsClient.getDocByPath(`users/${effectiveUser.uid}`, { db }), 3200),
            withProfileTimeout(fsClient.getDocByPath(`users/${effectiveUser.uid}/notificationSettings/main`, { db }), 3200).catch(() => null),
          ]);

          if (!mounted) return;
          const normalized = normalizeProfileData(profileDoc, effectiveUser, user);
          const normalizedWithHandoff =
            applyPhoneProfileHandoff(
              normalized,
              phoneProfileHandoff,
              effectiveUser
            );

          setData(normalizedWithHandoff);
          setLastSavedSnapshot(profileSnapshot(normalized));
          writeProfileFastCache(effectiveUser.uid, normalized);

          if (phoneProfileHandoff) {
            clearPhoneProfileHandoff();
          }
          const normalizedNotifications = normalizeUserNotificationSettings(notificationDoc);
          setNotificationSettings(normalizedNotifications);
          setLastSavedNotificationSettings(normalizedNotifications);
        } catch (e) {
          console.warn("ProfilePage iOS background load skipped", e);
        }
        return;
      }

      try {
        const uref = doc(db, "users", effectiveUser.uid);
        const snap = await getDoc(uref);
        if (!mounted) return;
        const normalized = normalizeProfileData(snap.exists() ? snap.data() : null, effectiveUser, user);
        const normalizedWithHandoff =
          applyPhoneProfileHandoff(
            normalized,
            phoneProfileHandoff,
            effectiveUser
          );

        setData(normalizedWithHandoff);
        setLastSavedSnapshot(profileSnapshot(normalized));

        if (phoneProfileHandoff) {
          clearPhoneProfileHandoff();
        }
        try {
          const nSnap = await getDoc(doc(db, "users", effectiveUser.uid, "notificationSettings", "main"));
          const normalizedNotifications = normalizeUserNotificationSettings(nSnap.exists() ? nSnap.data() : null);
          setNotificationSettings(normalizedNotifications);
          setLastSavedNotificationSettings(normalizedNotifications);
        } catch (_) {
          const fallbackNotifications = normalizeUserNotificationSettings();
          setNotificationSettings(fallbackNotifications);
          setLastSavedNotificationSettings(fallbackNotifications);
        }
      } catch (e) {
        console.warn("ProfilePage load error", e);
        if (mounted) setStatus({ type: "error", message: "No pudimos cargar todo tu perfil. Revisa tu conexión e intenta nuevamente." });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [db, user, effectiveUser]);

  useEffect(() => {
    return () => {
      phoneRequestInFlightRef.current = false;
      cancelPhoneLinkVerification().catch(() => null);
    };
  }, []);

  useEffect(() => {
    const uid = effectiveUser?.uid || "";
    const storedUntil = readPhoneOtpCooldownUntil(uid);
    setPhoneOtpCooldownUntil(storedUntil);
    setPhoneOtpCooldownSeconds(
      storedUntil > Date.now()
        ? Math.max(1, Math.ceil((storedUntil - Date.now()) / 1000))
        : 0
    );
  }, [effectiveUser?.uid]);

  useEffect(() => {
    if (!phoneOtpCooldownUntil || phoneOtpCooldownUntil <= Date.now()) {
      setPhoneOtpCooldownSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((phoneOtpCooldownUntil - Date.now()) / 1000)
      );
      setPhoneOtpCooldownSeconds(remaining);
      if (remaining <= 0) {
        setPhoneOtpCooldownUntil(0);
        writePhoneOtpCooldownUntil(effectiveUser?.uid, 0);
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [phoneOtpCooldownUntil, effectiveUser?.uid]);

  const startPhoneOtpCooldown = (seconds) => {
    const until = Date.now() + Math.max(1, Number(seconds) || 1) * 1000;
    setPhoneOtpCooldownUntil(until);
    setPhoneOtpCooldownSeconds(Math.max(1, Math.ceil((until - Date.now()) / 1000)));
    writePhoneOtpCooldownUntil(effectiveUser?.uid, until);
  };

  const clearPhoneOtpCooldown = () => {
    setPhoneOtpCooldownUntil(0);
    setPhoneOtpCooldownSeconds(0);
    writePhoneOtpCooldownUntil(effectiveUser?.uid, 0);
  };

  useEffect(() => {
    syncProfilePanelToUrl(activePanel);
  }, [activePanel]);

  const closeAccount = () => {
    if (hasUnsavedPreferenceChanges) { setPendingExit(true); return; }
    onClose?.();
  };
  useEffect(() => {
    const guard = (event) => { if (hasUnsavedPreferenceChanges) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [hasUnsavedPreferenceChanges]);
  useEffect(() => {
    let alive = true;
    const refresh = () => readPushPermission().then(value => { if (alive) setDevicePermission(value); });
    refresh(); window.addEventListener("focus", refresh);
    return () => { alive = false; window.removeEventListener("focus", refresh); };
  }, []);
  const switchPanel = (panel) => {
    setMobileMenu(false);
    const nextPanel = normalizeProfilePanel(panel);
    setActivePanel(nextPanel);
    setStatus({ type: "", message: "" });
    setNotificationStatus("");
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) {}
  };

  const handleChange = (key, value) => {
    setStatus({ type: "", message: "" });
    if (key === "phone") {
      setPhoneOtpStatus({ type: "", message: "" });
      setPhoneOtpSent(false);
      setPhoneOtpCode("");
      cancelPhoneLinkVerification().catch(() => null);
      setData((s) => {
        const nextPhoneE164 = normalizePhoneValue(value);
        const verifiedPhone = normalizePhoneValue(s.phoneVerifiedE164 || s.phoneE164 || "");
        const sameVerifiedPhone = Boolean(s.phoneVerified === true && nextPhoneE164 && verifiedPhone && nextPhoneE164 === verifiedPhone);
        return {
          ...s,
          phone: value,
          phoneE164: nextPhoneE164,
          phoneVerified: sameVerifiedPhone,
          phoneVerifiedE164: sameVerifiedPhone ? verifiedPhone : "",
          phoneVerificationProvider: sameVerifiedPhone ? (s.phoneVerificationProvider || "firebase_auth") : "",
          phoneVerifiedAt: sameVerifiedPhone ? s.phoneVerifiedAt : null,
        };
      });
      return;
    }
    if (key === "province") {
      setData((s) => {
        const nextProvince = String(value || "").trim();
        const currentCity = String(s.city || "").trim();
        const keepCity = nextProvince && cityBelongsToProvince(currentCity, nextProvince);
        return {
          ...s,
          province: nextProvince,
          city: keepCity ? currentCity : "",
        };
      });
      return;
    }

    setData((s) => ({ ...s, [key]: value }));
  };

  const handleNotificationChange = (key, value) => {
    setNotificationStatus("");
    setNotificationSettings((prev) => ({ ...normalizeUserNotificationSettings(prev), [key]: value === true }));
  };

  const handleSaveNotifications = async (settingsToSave = notificationSettings) => {
    if (!effectiveUser || !effectiveUser.uid) {
      setNotificationStatus("Debes iniciar sesión para guardar notificaciones.");
      return;
    }
    setNotificationSaving(true);
    setNotificationStatus("");
    try {
      const clean = notificationSnapshot(settingsToSave);
      if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
        await withProfileTimeout(
          fsClient.setDocByPath(`users/${effectiveUser.uid}/notificationSettings/main`, {
            ...clean,
            updatedAt: new Date().toISOString(),
            source: "profile_notifications",
          }, { merge: true, db }),
          10000
        );
      } else {
        await setDoc(doc(db, "users", effectiveUser.uid, "notificationSettings", "main"), {
          ...clean,
          updatedAt: serverTimestamp(),
          source: "profile_notifications",
        }, { merge: true });
      }
      setLastSavedNotificationSettings(normalizeUserNotificationSettings(settingsToSave));
      setNotificationStatus("Preferencias guardadas.");
      return true;
    } catch (err) {
      console.error("save notification settings error", err);
      setNotificationStatus("No pudimos guardar tus preferencias.");
      return false;
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleActivatePush = async () => {
    setPushRegistering(true);
    setNotificationStatus("");
    try {
      const res = await registerPushNotifications({ force: true });
      if (res && res.ok) {
        const next = { ...normalizeUserNotificationSettings(notificationSettings), pushEnabled: true };
        setNotificationSettings(next);
        setNotificationStatus("Notificaciones push activadas en este dispositivo.");
        setDevicePermission("granted");
        const saved = await handleSaveNotifications(next);
        if (saved) setNotificationStatus("Dispositivo conectado. Los avisos están activados.");
      } else {
        setNotificationStatus(`No se pudo activar push: ${res?.reason || "permiso no concedido"}.`);
      }
    } catch (err) {
      console.error("activate push failed", err);
      setNotificationStatus("No se pudo activar push en este dispositivo.");
    } finally {
      setPushRegistering(false);
    }
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!effectiveUser || !effectiveUser.uid) {
      setStatus({ type: "error", message: "Debes iniciar sesión para guardar tu perfil." });
      return false;
    }

    if (phoneChangedSinceLastSave && !phoneIsVerified) {
      setStatus({
        type: "error",
        message: "Verifica el teléfono por SMS antes de guardar el cambio. Los demás datos de tu perfil no se han perdido.",
      });
      setPhoneOtpStatus({
        type: "info",
        message: "Presiona ‘Verificar por SMS’, ingresa el código y el número quedará guardado automáticamente.",
      });
      return false;
    }

    setSaving(true);
    setStatus({ type: "", message: "" });

    try {
      const useNativeSafeTimestamp = isIosCapacitor();
      const payload = buildProfileSavePayload(data, effectiveUser, useNativeSafeTimestamp);

      await saveProfileDocSafe({ db, uid: effectiveUser.uid, payload });

      setLastSavedSnapshot(profileSnapshot(data));
      setLastSavedAt(new Date().toISOString());
      if (isIosCapacitor()) writeProfileFastCache(effectiveUser.uid, data);
      setStatus({ type: "success", message: "Perfil guardado correctamente." });
      return true;
    } catch (err) {
      console.error("save profile error", err);
      const raw = String(err?.message || err || "").toLowerCase();
      const message = raw.includes("403") || raw.includes("permission")
        ? "No se pudo guardar: permisos de Firestore. Revisa que tu cuenta de Apple esté vinculada al mismo usuario del negocio."
        : raw.includes("timeout")
          ? "No se pudo confirmar el guardado. Revisa conexión e intenta de nuevo."
          : "No pudimos guardar tu información. Intenta nuevamente.";
      setStatus({ type: "error", message });
      return false;
    } finally {
      setSaving(false);
    }
  };


  const handleSavePreferences = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!effectiveUser || !effectiveUser.uid) {
      setStatus({ type: "error", message: "Debes iniciar sesión para guardar tus preferencias." });
      return;
    }

    const shouldSaveProfilePreferences = hasUnsavedChanges;
    const shouldSaveNotificationPreferences = hasUnsavedNotificationChanges;

    if (!shouldSaveProfilePreferences && !shouldSaveNotificationPreferences) return;

    try {
      if (shouldSaveProfilePreferences) {
        const profileSaved = await handleSave();
        if (profileSaved === false) return;
      }
      if (shouldSaveNotificationPreferences) {
        const saved = await handleSaveNotifications();
        if (saved === false) return;
      }
      setStatus({ type: "success", message: "Cambios guardados." });
    } catch (err) {
      console.error("save profile preferences error", err);
      setStatus({ type: "error", message: "No pudimos guardar tus preferencias. Intenta nuevamente." });
    }
  };

  const handleRequestPhoneOtp = async () => {
    if (!effectiveUser || !effectiveUser.uid) {
      setPhoneOtpStatus({ type: "error", message: "Debes iniciar sesión para verificar tu teléfono." });
      return;
    }

    const runtimeDebug = getCapacitorRuntimeDebug();
    const runtimePlatform = String(
      runtimeDebug.importedPlatform ||
      runtimeDebug.windowPlatform ||
      ""
    ).trim().toLowerCase();
    const isNativeRuntime = Boolean(
      runtimeDebug.importedIsNative === true ||
      runtimeDebug.hasNativeBridge === true ||
      runtimePlatform === "ios" ||
      runtimePlatform === "android"
    );
    const hostname =
      typeof window !== "undefined"
        ? String(window.location?.hostname || "").trim().toLowerCase()
        : "";
    const isLocalBrowser =
      !isNativeRuntime &&
      (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "::1" ||
        hostname === "[::1]"
      );

    if (isLocalBrowser) {
      setPhoneOtpStatus({
        type: "error",
        message: "La verificación por SMS no está disponible desde el entorno local. Usa la app móvil o la versión web publicada.",
      });
      return;
    }

    if (phoneRequestInFlightRef.current || phoneOtpSending || phoneOtpVerifying) {
      return;
    }
    if (phoneOtpCoolingDown) {
      setPhoneOtpStatus({
        type: "info",
        message: `Podrás solicitar otro código en ${formatPhoneOtpCooldown(phoneOtpCooldownSeconds)}.`,
      });
      return;
    }
    if (!canRequestPhoneOtp) {
      setPhoneOtpStatus({ type: "error", message: "Agrega un número de teléfono válido antes de verificar." });
      return;
    }

    phoneRequestInFlightRef.current = true;
    setPhoneOtpSending(true);
    setPhoneOtpStatus({ type: "", message: "" });
    setPhoneOtpCode("");
    try {
      // Invisible reCAPTCHA must be attached to the button that starts
      // the SMS request. Use the currently rendered button for first send
      // or resend so Firebase can validate the web request correctly.
      const recaptchaButtonId = phoneOtpSent
        ? "profile-phone-resend-sms-button"
        : "profile-phone-send-sms-button";

      const result = await startPhoneLinkVerification({
        phoneE164: profilePhoneE164,
        recaptchaContainerId: recaptchaButtonId,
      });

      if (result?.verified === true || result?.autoCompleted === true) {
        const nextData = {
          ...data,
          phone: result.phoneDisplay || profilePhoneDisplay || data.phone,
          phoneDisplay: result.phoneDisplay || profilePhoneDisplay || data.phone,
          phoneE164: result.phoneE164 || result.phoneVerifiedE164 || profilePhoneE164,
          phoneCountryCode: result.phoneCountryCode || data.phoneCountryCode || "PA",
          phoneDialCode: result.phoneDialCode || data.phoneDialCode || "+507",
          phoneNational: result.phoneNational || data.phoneNational || "",
          phoneVerified: true,
          phoneVerifiedE164: result.phoneVerifiedE164 || result.phoneE164 || profilePhoneE164,
          phoneVerifiedAt: result.phoneVerifiedAt || new Date().toISOString(),
          phoneVerificationProvider: "firebase_auth",
        };
        setData(nextData);
        setLastSavedSnapshot((previous) =>
          mergeVerifiedPhoneIntoSavedSnapshot(previous, nextData)
        );
        setLastSavedAt(new Date().toISOString());
        setPhoneOtpSent(false);
        clearPhoneOtpCooldown();
        setPhoneOtpStatus({ type: "success", message: "Teléfono verificado correctamente." });
        setStatus({ type: "success", message: "Tu teléfono quedó verificado en tu cuenta Loop." });
        return;
      }

      setPhoneOtpSent(true);
      startPhoneOtpCooldown(PHONE_OTP_RESEND_COOLDOWN_SECONDS);
      setPhoneOtpStatus({
        type: "success",
        message: `Te enviamos un código SMS a ${profilePhoneDisplay}.`,
      });
    } catch (err) {
      console.error("request Firebase phone verification failed", err);
      const rawError = String(err?.code || err?.message || err || "").toLowerCase();

      if (rawError.includes("provider-already-linked")) {
        try {
          const recovered = await syncCurrentVerifiedPhoneIdentity({
            source: "profile_phone_provider_already_linked_recovery_r32",
          });
          const recoveredPhone = normalizePhoneValue(
            recovered?.phoneVerifiedE164 || recovered?.phoneE164 || ""
          );
          if (recoveredPhone && recoveredPhone === profilePhoneE164) {
            const nextData = {
              ...data,
              phone: recovered.phoneDisplay || profilePhoneDisplay || data.phone,
              phoneDisplay: recovered.phoneDisplay || profilePhoneDisplay || data.phone,
              phoneE164: recoveredPhone,
              phoneCountryCode: recovered.phoneCountryCode || data.phoneCountryCode || "PA",
              phoneDialCode: recovered.phoneDialCode || data.phoneDialCode || "+507",
              phoneNational: recovered.phoneNational || data.phoneNational || "",
              phoneVerified: true,
              phoneVerifiedE164: recoveredPhone,
              phoneVerifiedAt: recovered.phoneVerifiedAt || new Date().toISOString(),
              phoneVerificationProvider: "firebase_auth",
            };
            setData(nextData);
            setLastSavedSnapshot((previous) =>
              mergeVerifiedPhoneIntoSavedSnapshot(previous, nextData)
            );
            setLastSavedAt(new Date().toISOString());
            setPhoneOtpSent(false);
            setPhoneOtpStatus({ type: "success", message: "Teléfono verificado y sincronizado correctamente." });
            setStatus({ type: "success", message: "Tu teléfono verificado quedó sincronizado con Loop." });
            return;
          }
        } catch (syncError) {
          console.error("recover existing Firebase phone link failed", syncError);
        }
      }

      setPhoneOtpSent(false);
      if (isPhoneRateLimitError(err)) {
        startPhoneOtpCooldown(phoneRateLimitCooldownSeconds(err));
      }
      setPhoneOtpStatus({ type: "error", message: phoneErrorMessage(err) });
    } finally {
      phoneRequestInFlightRef.current = false;
      setPhoneOtpSending(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!effectiveUser || !effectiveUser.uid) {
      setPhoneOtpStatus({ type: "error", message: "Debes iniciar sesión para verificar tu teléfono." });
      return;
    }
    const code = String(phoneOtpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setPhoneOtpStatus({ type: "error", message: "Ingresa el código de 6 dígitos." });
      return;
    }

    setPhoneOtpVerifying(true);
    setPhoneOtpStatus({ type: "", message: "" });
    try {
      const payload = await confirmPhoneLinkVerification({ code });
      const nextData = {
        ...data,
        phone: payload.phoneDisplay || profilePhoneDisplay || data.phone,
        phoneDisplay: payload.phoneDisplay || profilePhoneDisplay || data.phone,
        phoneE164: payload.phoneE164 || payload.phoneVerifiedE164 || profilePhoneE164,
        phoneCountryCode: payload.phoneCountryCode || data.phoneCountryCode || "PA",
        phoneDialCode: payload.phoneDialCode || data.phoneDialCode || "+507",
        phoneNational: payload.phoneNational || data.phoneNational || "",
        phoneVerified: true,
        phoneVerifiedE164: payload.phoneVerifiedE164 || payload.phoneE164 || profilePhoneE164,
        phoneVerifiedAt: payload.phoneVerifiedAt || new Date().toISOString(),
        phoneVerificationProvider: "firebase_auth",
      };
      setData(nextData);
      setLastSavedSnapshot((previous) =>
        mergeVerifiedPhoneIntoSavedSnapshot(previous, nextData)
      );
      setLastSavedAt(new Date().toISOString());
      setPhoneOtpSent(false);
      setPhoneOtpCode("");
      clearPhoneOtpCooldown();
      setPhoneOtpStatus({ type: "success", message: "Teléfono verificado correctamente." });
      setStatus({ type: "success", message: "Tu teléfono quedó verificado en tu cuenta Loop." });
    } catch (err) {
      console.error("verify Firebase phone code failed", err);
      setPhoneOtpStatus({ type: "error", message: phoneErrorMessage(err) });
    } finally {
      setPhoneOtpVerifying(false);
    }
  };


  const handleDeleteAccount = async () => {
    if (!effectiveUser || !effectiveUser.uid) {
      setStatus({ type: "error", message: "Debes iniciar sesión para eliminar tu cuenta." });
      return;
    }

    if (!deleteAck) {
      setStatus({ type: "error", message: "Confirma que entiendes que esta acción es permanente." });
      return;
    }

    if (shouldAskDeletePassword && !deletePassword) {
      setStatus({ type: "error", message: "Ingresa tu contraseña para confirmar la eliminación." });
      return;
    }

    setDeletingAccount(true);
    setStatus({ type: "", message: "" });

    try {
      const reauth = await reauthenticateForAccountDelete({ auth, password: deletePassword });
      if (!reauth.ok) {
        setStatus({
          type: "error",
          message: "Por seguridad, vuelve a iniciar sesión y luego intenta eliminar tu cuenta nuevamente.",
        });
        setDeletingAccount(false);
        return;
      }

      const idToken = await getDeleteAccountIdToken(auth);
      if (!idToken) {
        setStatus({ type: "error", message: "Por seguridad, vuelve a iniciar sesión y luego intenta eliminar tu cuenta nuevamente." });
        setDeletingAccount(false);
        return;
      }

      await httpRequestJson({
        url: getDeleteAccountEndpoint(),
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
        data: { confirm: true },
      });

      clearLocalAuthAfterDelete();
      try {
        if (isIosCapacitor() && isFirebaseNativeAvailable()) {
          await nativeFirebaseSignOut();
        }
      } catch (_) {}
      try { if (auth) await signOut(auth); } catch (_) {}
      clearLocalAuthAfterDelete();

      setStatus({ type: "success", message: "Tu cuenta fue eliminada correctamente." });
      setDeleteConfirmOpen(false);
      setDeletePassword("");
      setDeleteAck(false);

      setTimeout(() => {
        try { window.location.replace("/login?accountDeleted=1"); }
        catch (_) { window.location.href = "/login?accountDeleted=1"; }
      }, 700);
    } catch (err) {
      console.error("delete account error", err);
      const msg = String(err && err.message ? err.message : err || "");
      const needsLogin = msg.includes("auth/requires-recent-login") || msg.includes("requires-recent-login") || msg.includes("401") || msg.includes("unauthenticated");
      setStatus({
        type: "error",
        message: needsLogin
          ? "Por seguridad, vuelve a iniciar sesión y luego intenta eliminar tu cuenta nuevamente."
          : "No pudimos eliminar tu cuenta. Revisa tu conexión e intenta nuevamente.",
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const copyToClipboard = async (value) => {
    const clean = String(value || "").trim();
    if (!clean) return;

    try {
      await navigator.clipboard.writeText(clean);
      setStatus({ type: "success", message: "Copiado al portapapeles." });
    } catch (_) {
      setStatus({ type: "error", message: "No se pudo copiar automáticamente." });
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    setStatus({ type: "", message: "" });
    try {
      clearLocalAuthAfterDelete();
      try {
        if (isIosCapacitor() && isFirebaseNativeAvailable()) {
          await nativeFirebaseSignOut();
        }
      } catch (_) {}
      try { if (auth) await signOut(auth); } catch (_) {}
      clearLocalAuthAfterDelete();
      const logoutTarget = getSafeAuthRedirectPath("/", "/");
      try { window.location.replace(logoutTarget); }
      catch (_) { window.location.href = logoutTarget; }
    } catch (err) {
      console.error("sign out error", err);
      setStatus({ type: "error", message: "No pudimos cerrar sesión. Intenta nuevamente." });
      setSigningOut(false);
    }
  };

  const openDeleteAccountModal = () => {
    setDeletePassword("");
    setDeleteAck(false);
    setDeleteConfirmOpen(true);
    setStatus({ type: "", message: "" });
  };

  const panelMeta = PROFILE_PANEL_META[activePanel] || PROFILE_PANEL_META.profile;

  const profileSaveLabel = saving
    ? "Guardando..."
    : hasUnsavedChanges
      ? "Guardar cambios"
      : "Guardado";

  if (loading) return <Skeleton />;

  if (!effectiveUser) {
    return (
      <div className="profile-page">
        <style>{profileStyles}</style>
        <div className="profile-shell">
          <div className="profile-empty">
            <div className="profile-empty-icon">L</div>
            <h2>{t("Inicia sesión")}</h2>
            <p>{t("Necesitas estar conectado para ver y editar tu perfil.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`profile-page profile-v61 profile-v62 profile-phone-r35 account-refresh ${mobileMenu ? "account-menu-open" : "account-section-open"}`}>
      <style>{profileStyles}</style>

      {status.message ? <div role="status" className={`profile-toast ${status.type === "success" ? "is-success" : "is-error"}`}>{t(status.message)}</div> : null}

      <div className="profile-v61-shell">
        <aside className="profile-v61-sidebar" aria-label={t("Ajustes de cuenta")}>
          <div className="profile-v62-sidebar-title">
            <div className="profile-kicker">{t("Cuenta Loop")}</div>
            <div className="profile-v62-sidebar-title-row">
              <div className="profile-v61-avatar">{initials}</div>
              <div>
                <strong>{fullName || t("Mi cuenta")}</strong>
                <span>{t("Mi cuenta")}</span>
              </div>
            </div>
          </div>

          <nav className="profile-v61-nav" aria-label={t("Secciones del perfil")}>
            <button type="button" className={activePanel === "profile" ? "is-active" : ""} onClick={() => switchPanel("profile")}>
              <span>{t("Datos personales")}</span>
              <em>{t("Datos y contacto")}</em>
            </button>
            <button type="button" className={activePanel === "preferences" ? "is-active" : ""} onClick={() => switchPanel("preferences")}>
              <span>{t("Notificaciones")}</span>
              <em>{t("Reservas y avisos")}</em>
            </button>
            <button type="button" className={activePanel === "payments" ? "is-active" : ""} onClick={() => switchPanel("payments")}>
<span>{t("Mis pagos")}</span>
<em>{t("Historial y recibos")}</em>
</button>
            <button type="button" className={activePanel === "security" ? "is-active" : ""} onClick={() => switchPanel("security")}>
              <span>{t("Acceso y seguridad")}</span>
              <em>{t("Cuenta")}</em>
            </button>
          </nav>

          <div className="profile-v61-sidebar-actions">
            <LanguagePicker />
            {typeof onClose === "function" ? (
              <button type="button" className="profile-secondary-btn" onClick={closeAccount} disabled={saving}>{t("Volver")}</button>
            ) : null}
          </div>
        </aside>

        <main className="profile-v61-main">
          <header className="profile-v61-header">
            <div>
              <button className="account-back" type="button" onClick={() => setMobileMenu(true)}>{t("← Mi cuenta")}</button>
              <h1>{t(panelMeta.title)}</h1>
              <p>{t(panelMeta.description)}</p>
            </div>
            <div className="profile-v61-header-actions">
              {(hasUnsavedPreferenceChanges || saving || notificationSaving) && (
                <button type="button" className="profile-save-btn" onClick={handleSavePreferences} disabled={saving || notificationSaving}>
                  {saving || notificationSaving ? "Guardando…" : t("Guardar cambios")}
                </button>
              )}
            </div>
          </header>

          <form onSubmit={activePanel === "preferences" ? handleSavePreferences : activePanel === "profile" ? handleSave : (event) => event.preventDefault()} className="profile-v61-content">
            {activePanel === "profile" ? (
              <>
                <section className="profile-v61-card profile-v62-profile-card">
                  <div className="profile-v61-section-title profile-v62-section-title-clean">
                    <div>
                      <span>{t("Información personal")}</span>
                      <h2>{t("Datos principales")}</h2>
                    </div>
                    <p>{t("Solo lo necesario para identificarte y contactarte en tus reservas.")}</p>
                  </div>

                  <div className="profile-fields-grid profile-fields-grid-minimal">
                    <TextField label={t("Nombre")} value={data.displayName} onChange={(value) => handleChange("displayName", value)} icon="user" placeholder={t("Tu nombre")} autoComplete="given-name" />
                    <TextField label={t("Apellido")} value={data.apellido} onChange={(value) => handleChange("apellido", value)} icon="user" placeholder={t("Tu apellido")} autoComplete="family-name" />
                    <div className="account-contact-field"><TextField label={t("Correo de la cuenta")} value={effectiveUser?.email || data.email} readOnly type="email" icon="mail" placeholder={t("Sin correo asociado")} autoComplete="email" /><p className="account-hint">{t("Este correo no se puede modificar desde el perfil.")}</p></div>
                    <div className="account-phone-field"><PhoneField label={t("Teléfono móvil")} value={data.phone} onChange={(value) => handleChange("phone", value)} />{phoneIsVerified && <span className="account-verified" role="status">{t("✓ Verificado")}</span>}</div>
                  </div>

                  <div hidden={phoneIsVerified} className={`profile-phone-verification is-${phoneVerificationMode}`}>
                    <div className="profile-phone-verification-copy">
                      <span className="profile-phone-verification-icon" aria-hidden="true">
                        {phoneIsVerified ? "✓" : phoneOtpSent ? "6" : "SMS"}
                      </span>
                      <div>
                        <strong>{phoneVerificationTitle}</strong>
                        <p>{phoneVerificationDescription}</p>
                      </div>
                    </div>

                    {!phoneIsVerified ? (
                      <div className="profile-phone-verification-actions">
                        {!phoneOtpSent ? (
                          <button
                            id="profile-phone-send-sms-button"
                            type="button"
                            className="profile-phone-primary-action"
                            onClick={handleRequestPhoneOtp}
                            disabled={phoneOtpSending || phoneOtpVerifying || !canRequestPhoneOtp}
                          >
                            {phoneOtpSending
                              ? "Enviando SMS..."
                              : phoneOtpCoolingDown
                                ? `Reintentar en ${formatPhoneOtpCooldown(phoneOtpCooldownSeconds)}`
                                : "Verificar por SMS"}
                          </button>
                        ) : (
                          <>
                            <label className="profile-phone-code-field">
                              <span>{t("Código de 6 dígitos")}</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="[0-9]*"
                                maxLength={6}
                                value={phoneOtpCode}
                                onChange={(event) => setPhoneOtpCode(String(event.target.value || "").replace(/\D/g, "").slice(0, 6))}
                                placeholder="000000"
                                aria-label={t("Código SMS de 6 dígitos")}
                              />
                            </label>
                            <button
                              id="profile-phone-confirm-code-button"
                              type="button"
                              className="profile-phone-primary-action"
                              onClick={handleVerifyPhoneOtp}
                              disabled={phoneOtpVerifying || phoneOtpSending || phoneOtpCode.length !== 6}
                            >
                              {phoneOtpVerifying ? "Verificando..." : "Confirmar código"}
                            </button>
                            <button
                              id="profile-phone-resend-sms-button"
                              type="button"
                              className="profile-phone-secondary-action"
                              onClick={handleRequestPhoneOtp}
                              disabled={phoneOtpSending || phoneOtpVerifying || phoneOtpCoolingDown}
                            >
                              {phoneOtpSending
                                ? "Reenviando..."
                                : phoneOtpCoolingDown
                                  ? `Reenviar en ${formatPhoneOtpCooldown(phoneOtpCooldownSeconds)}`
                                  : "Reenviar SMS"}
                            </button>
                          </>
                        )}
                      </div>
                    ) : null}

                    {phoneOtpStatus.message ? (
                      <div className={`profile-phone-status ${phoneOtpStatus.type === "error" ? "is-error" : phoneOtpStatus.type === "info" ? "is-info" : "is-success"}`} role="status">
                        {t(phoneOtpStatus.message)}
                      </div>
                    ) : null}
                  </div>

                  <details className="account-optional"><summary>{t("Nacimiento y ubicación")}</summary>

                  <div className="profile-v62-subsection-head">
                    <span>{t("Detalles opcionales")}</span>
                    <p>{t("Nacimiento y ubicación principal.")}</p>
                  </div>

                  <div className="profile-fields-grid profile-optional-fields profile-v62-optional-inline">
                    <TextField label={t("Fecha de nacimiento")} value={data.birthDate} onChange={(value) => handleChange("birthDate", value)} type="date" icon="calendar" />
                    <LocationSearchInput label={t("Zona o ciudad (opcional)")} selected={Boolean(data.province)} value={[data.city,data.province].filter(Boolean).join(", ")} onTextChange={(value) => setData(prev => ({...prev, city:value,province:""}))} onSelect={(place) => setData(prev => ({...prev, city:place.zone,province:place.province}))} />
                  </div>
                  </details>
                </section>
              </>
            ) : activePanel === "preferences" ? (
              <>
                <section className="profile-v61-card account-notifications">
                  <h2>{t("Mis reservas")}</h2>
                  <NotificationPreferenceRow title={t("Cambios en mis reservas")} copy="Confirmaciones, cancelaciones y cambios de horario." checked={notificationSettings.clientBookingUpdates} onChange={(value) => handleNotificationChange("clientBookingUpdates", value)} />
                  <NotificationPreferenceRow title={t("Recordatorios")} copy="Recibe un aviso antes de tu cita." checked={notificationSettings.clientBookingReminders && data.preferredReminderTiming !== "none"} onChange={(value) => { if (value && data.preferredReminderTiming === "none") handleChange("preferredReminderTiming", "24h_2h"); handleNotificationChange("clientBookingReminders", value); }} />
                  {notificationSettings.clientBookingReminders && data.preferredReminderTiming !== "none" && <label className="account-timing">{t("Cuándo avisarme")}<select value={data.preferredReminderTiming} onChange={event => handleChange("preferredReminderTiming", event.target.value)}>
                    <option value="24h_2h">{t("24 h y 2 h antes")}</option><option value="24h">{t("24 h antes")}</option><option value="2h">{t("2 h antes")}</option>
                  </select></label>}
                  <h2>{t("Cómo recibirlos")}</h2>
                  <NotificationPreferenceRow title={t("Correo electrónico")} copy="Avisos de reservas por correo." checked={notificationSettings.emailEnabled} onChange={(value) => handleNotificationChange("emailEnabled", value)} />
                  <NotificationPreferenceRow title="WhatsApp" copy="Recordatorios, cuando el negocio tenga este canal disponible." checked={notificationSettings.whatsappClientReminders} onChange={(value) => handleNotificationChange("whatsappClientReminders", value)} />
                  <NotificationPreferenceRow title={t("Avisos en mis dispositivos")} copy="Activa los avisos de Loop en los dispositivos con permiso." checked={notificationSettings.pushEnabled} onChange={(value) => handleNotificationChange("pushEnabled", value)} />
                  <div className="account-device"><div><strong>{t("Este dispositivo")}</strong><p>{devicePermission === "granted" ? "Permiso concedido. Puedes comprobar la conexión con Loop." : devicePermission === "denied" ? "Permiso bloqueado. Habilítalo en los ajustes del navegador o del teléfono." : devicePermission === "unsupported" ? "Los avisos no están disponibles en este navegador." : "Comprueba el permiso y conecta este dispositivo."}</p></div>
                    {devicePermission !== "denied" && devicePermission !== "unsupported" && <button type="button" className="profile-outline-action" onClick={handleActivatePush} disabled={pushRegistering || notificationSaving}>{pushRegistering ? "Conectando…" : devicePermission === "granted" ? "Conectar dispositivo" : "Activar avisos"}</button>}
                  </div>
                  {notificationStatus && <p role="status" className="profile-notification-status">{notificationStatus}</p>}
                  <h2>{t("Novedades de Loop")}</h2>
                  <NotificationPreferenceRow title={t("Promociones por correo")} copy="Opcional. Puedes desactivarlas cuando quieras." checked={notificationSettings.marketingEmails} onChange={(value) => handleNotificationChange("marketingEmails", value)} />
                </section>
              </>
            ) : activePanel === "payments" ? (
<section className="profile-v61-card profile-v61-memberships-card">
<MyPaymentsPage embedded />
</section>
            ) : (
              <>
                <section className="profile-v61-card profile-v61-settings-list">
                  <div className="profile-v61-setting-row">
                    <div>
                      <span>{t("Correo de cuenta")}</span>
                      <strong>{effectiveUser.email || t("No disponible")}</strong>
                    </div>

                  </div>
                  <AccountAccessMethods key={effectiveUser.uid} uid={effectiveUser.uid} />
                  <div className="profile-v61-setting-row">
                    <div>
                      <span>{t("Sesión")}</span>
                      <strong>{t("Cierra sesión en este dispositivo")}</strong>
                    </div>
                    <button type="button" className="profile-outline-action" onClick={handleSignOut} disabled={signingOut}>
                      {signingOut ? "Cerrando..." : t("Cerrar sesión")}
                    </button>
                  </div>
                  {shouldAskDeletePassword && <div className="profile-v61-setting-row"><div><strong>{t("Contraseña")}</strong><p>{t("Recibe un enlace para restablecerla.")}</p></div><a className="profile-outline-action" href="/forgot-password">{t("Restablecer contraseña")}</a></div>}
                </section>

                <section className="profile-danger-zone">
                  <div>
                    <div className="profile-danger-kicker">{t("Zona avanzada")}</div>
                    <h3>{t("Eliminar cuenta")}</h3>
                    <p>{t("Elimina tu acceso a Loop. Revisa la confirmación antes de continuar.")}</p>
                  </div>
                  <button type="button" className="profile-delete-link-btn stronger" onClick={openDeleteAccountModal} disabled={saving || deletingAccount}>{t("Eliminar cuenta")}</button>
                </section>
              </>
            )}
          </form>
        </main>
      </div>

      {pendingExit && <div className="account-exit" ref={exitDialogRef} role="dialog" aria-modal="true" aria-label={t("Cambios sin guardar")}><section><h2>{t("Tienes cambios sin guardar")}</h2><p>{t("Puedes volver para guardarlos o salir y descartarlos.")}</p><button type="button" autoFocus onClick={() => setPendingExit(false)}>{t("Seguir editando")}</button><button type="button" onClick={() => {setPendingExit(false);onClose?.();}}>{t("Descartar y salir")}</button></section></div>}
      {deleteConfirmOpen ? (
        <div className="profile-delete-modal" role="dialog" aria-modal="true" aria-label={t("Confirmar eliminación de cuenta")}>
          <button type="button" className="profile-delete-backdrop" aria-label={t("Cerrar confirmación")} onClick={() => !deletingAccount && setDeleteConfirmOpen(false)} />
          <div className="profile-delete-panel pro">
            <div className="profile-delete-panel-head">
              <div className="profile-delete-shield-icon"><FieldIcon type="shield" /></div>
              <div>
                <div className="profile-delete-panel-kicker">{t("Confirmación requerida")}</div>
                <h2>{t("¿Eliminar tu cuenta?")}</h2>
              </div>
            </div>

            <p className="profile-delete-summary">{t("Esta acción eliminará tu cuenta de Loop y cerrará tu sesión en este dispositivo. No podrás deshacerla después de confirmar.")}</p>

            <div className="profile-delete-info-box">
              <div><span>{t("Cuenta")}</span><strong>{data.email || effectiveUser.email || "Usuario conectado"}</strong></div>
              <div><span>{t("Acción")}</span><strong>{t("Eliminación permanente")}</strong></div>
            </div>

            {shouldAskDeletePassword ? (
              <label className="profile-delete-password-field">
                <span>{t("Confirma tu contraseña")}</span>
                <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder={t("Contraseña actual")} autoComplete="current-password" disabled={deletingAccount} />
              </label>
            ) : (
              <div className="profile-delete-social-note">{t("Por seguridad, si tu sesión no es reciente, tendrás que volver a iniciar sesión antes de eliminar la cuenta.")}</div>
            )}

            <label className="profile-delete-ack">
              <input type="checkbox" checked={deleteAck} onChange={(e) => setDeleteAck(e.target.checked)} disabled={deletingAccount} />
              <span>{t("Entiendo que esta acción es permanente y no se puede deshacer.")}</span>
            </label>

            <div className="profile-delete-actions pro">
              <button type="button" className="profile-keep-account-btn" onClick={() => setDeleteConfirmOpen(false)} disabled={deletingAccount}>{t("Mantener cuenta")}</button>
              <button type="button" className="profile-confirm-delete-btn" onClick={handleDeleteAccount} disabled={deletingAccount}>
                {deletingAccount ? "Eliminando..." : "Sí, eliminar cuenta"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const profileStyles = `
  :root {
    --loop-teal: #00675f;
    --loop-teal-2: #00786f;
    --loop-ink: #111827;
    --loop-muted: #607086;
    --loop-soft: #f8faf8;
    --loop-cream: #fbf7ef;
    --loop-line: #dbe7e5;
    --loop-card: rgba(255,255,255,.92);
    --loop-shadow: 0 24px 70px rgba(15, 40, 39, .08);
  }

  .profile-page {
    min-height: calc(100vh - 64px);
    background:
      radial-gradient(circle at 12% 8%, rgba(0, 103, 95, .08), transparent 30%),
      radial-gradient(circle at 90% 0%, rgba(245, 181, 83, .10), transparent 34%),
      linear-gradient(180deg, #fffdf8 0%, #f8fbfa 55%, #ffffff 100%);
    color: var(--loop-ink);
    font-family: inherit;
    overflow-x: hidden;
  }

  .profile-v61-shell {
    width: min(100%, 1320px);
    margin: 0 auto;
    padding: 34px 28px 72px;
    display: grid;
    grid-template-columns: 286px minmax(0, 1fr);
    gap: 28px;
    align-items: start;
  }

  .profile-v61-sidebar {
    position: sticky;
    top: 84px;
    align-self: start;
    display: grid;
    gap: 14px;
  }

  .profile-v61-account,
  .profile-v61-nav,
  .profile-v61-card,
  .profile-v61-details-card,
  .profile-danger-zone {
    background: var(--loop-card);
    border: 1px solid var(--loop-line);
    border-radius: 24px;
    box-shadow: var(--loop-shadow);
    backdrop-filter: blur(14px);
  }

  .profile-v61-account {
    padding: 18px;
  }

  .profile-v61-avatar {
    width: 54px;
    height: 54px;
    border-radius: 18px;
    display: grid;
    place-items: center;
    background: linear-gradient(145deg, rgba(0, 103, 95, .14), rgba(0, 137, 127, .08));
    color: var(--loop-teal);
    border: 1px solid rgba(0, 103, 95, .15);
    font-size: 20px;
    font-weight: 950;
    letter-spacing: -.04em;
    flex: 0 0 auto;
  }

  .profile-v61-avatar.is-large {
    width: 64px;
    height: 64px;
    border-radius: 21px;
    font-size: 24px;
  }

  .profile-v61-account-copy {
    margin-top: 14px;
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  .profile-v61-account-copy strong {
    font-size: 18px;
    line-height: 1.05;
    letter-spacing: -.04em;
    color: #111827;
  }

  .profile-v61-account-copy span {
    font-size: 12px;
    color: var(--loop-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 750;
  }

  .profile-v61-account-meta {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .profile-v61-account-meta span,
  .profile-v61-status-chip {
    border: 1px solid rgba(0, 103, 95, .15);
    background: rgba(231, 249, 244, .78);
    color: var(--loop-teal);
    font-size: 11px;
    font-weight: 900;
    border-radius: 999px;
    padding: 6px 10px;
  }

  .profile-v61-progress {
    height: 7px;
    background: #ecf3f2;
    border-radius: 999px;
    overflow: hidden;
    margin-top: 16px;
  }

  .profile-v61-progress i {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, var(--loop-teal), #11a28f);
    border-radius: inherit;
  }

  .profile-v61-nav {
    padding: 8px;
    display: grid;
    gap: 4px;
  }

  .profile-v61-nav button {
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    border-radius: 17px;
    padding: 13px 14px;
    cursor: pointer;
    display: grid;
    gap: 4px;
    color: #111827;
  }

  .profile-v61-nav button span {
    font-size: 14px;
    font-weight: 950;
    letter-spacing: -.02em;
  }

  .profile-v61-nav button em {
    font-size: 11.5px;
    color: var(--loop-muted);
    font-style: normal;
    font-weight: 750;
  }

  .profile-v61-nav button.is-active {
    background: linear-gradient(135deg, rgba(0,103,95,.11), rgba(0,103,95,.05));
    box-shadow: inset 3px 0 0 var(--loop-teal);
  }

  .profile-v61-sidebar-actions {
    display: flex;
  }

  .profile-v61-main {
    min-width: 0;
    display: grid;
    gap: 16px;
  }

  .profile-v61-header {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 20px;
    padding: 8px 0 8px;
  }

  .profile-kicker,
  .profile-eyebrow,
  .profile-v61-section-title span,
  .profile-v61-identity span,
  .profile-v61-settings-list span,
  .profile-danger-kicker,
  .profile-delete-panel-kicker {
    color: var(--loop-teal);
    text-transform: uppercase;
    letter-spacing: .17em;
    font-size: 11px;
    font-weight: 950;
  }

  .profile-v61-header h1 {
    margin: 6px 0 6px;
    font-size: clamp(40px, 5vw, 64px);
    line-height: .92;
    letter-spacing: -.075em;
    color: #101827;
  }

  .profile-v61-header p {
    margin: 0;
    color: var(--loop-muted);
    font-size: 14px;
    font-weight: 750;
  }

  .profile-v61-header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .profile-save-state {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    min-height: 42px;
    border: 1px solid var(--loop-line);
    background: rgba(255,255,255,.78);
    border-radius: 18px;
    padding: 8px 12px;
    color: var(--loop-muted);
    font-size: 12px;
    font-weight: 850;
  }

  .profile-save-state.is-dirty {
    border-color: rgba(245, 158, 11, .35);
    background: rgba(255, 250, 235, .8);
    color: #92400e;
  }

  .profile-save-state button {
    border: 0;
    border-radius: 999px;
    background: var(--loop-teal);
    color: white;
    font-weight: 950;
    padding: 8px 12px;
    cursor: pointer;
  }

  .profile-v61-content {
    display: grid;
    gap: 16px;
  }

  .profile-v61-card {
    padding: 22px;
  }

  .profile-v61-identity {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 16px;
    align-items: center;
    min-height: 116px;
  }

  .profile-v61-identity h2 {
    margin: 5px 0 4px;
    font-size: clamp(24px, 3.4vw, 38px);
    line-height: .95;
    letter-spacing: -.06em;
  }

  .profile-v61-identity p,
  .profile-v61-section-title p,
  .profile-v61-row-card p {
    margin: 0;
    color: var(--loop-muted);
    font-weight: 750;
    font-size: 13px;
  }

  .profile-v61-soft-icon {
    width: 58px;
    height: 58px;
    border-radius: 18px;
    display: grid;
    place-items: center;
    color: var(--loop-teal);
    background: rgba(0, 103, 95, .08);
    border: 1px solid rgba(0, 103, 95, .14);
  }

  .profile-v61-section-title {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: end;
    border-bottom: 1px solid #e6eeee;
    padding-bottom: 16px;
    margin-bottom: 16px;
  }

  .profile-v61-section-title h2 {
    margin: 5px 0 0;
    font-size: 24px;
    letter-spacing: -.05em;
  }

  .profile-fields-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .profile-field {
    display: grid;
    gap: 8px;
    min-width: 0;
  }

  .profile-field-wide {
    grid-column: 1 / -1;
  }

  .profile-field-label {
    font-size: 12px;
    font-weight: 950;
    color: #334155;
  }

  .profile-input-wrap,
  .profile-phone-combo {
    min-height: 52px;
    display: flex;
    align-items: center;
    border: 1px solid #d9e6e4;
    border-radius: 16px;
    background: rgba(255,255,255,.9);
    overflow: hidden;
    transition: border-color .16s ease, box-shadow .16s ease, background .16s ease;
  }

  .profile-input-wrap:focus-within,
  .profile-phone-combo:focus-within {
    border-color: rgba(0,103,95,.42);
    box-shadow: 0 0 0 4px rgba(0,103,95,.08);
    background: white;
  }

  .profile-input-icon {
    width: 44px;
    display: grid;
    place-items: center;
    color: #8ca0b3;
    flex: 0 0 auto;
  }

  .profile-input-wrap input,
  .profile-input-wrap select,
  .profile-input-wrap textarea,
  .profile-phone-local {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    color: #111827;
    font-weight: 850;
    padding: 0 14px 0 0;
  }

  .profile-input-wrap select {
    min-height: 50px;
    cursor: pointer;
  }

  .profile-select-field {
    position: relative;
  }

  .profile-select-wrap {
    position: relative;
    min-width: 0;
    z-index: 1;
  }

  .profile-select-wrap.is-open {
    z-index: 80;
  }

  .profile-select-button {
    width: 100%;
    min-height: 52px;
    border: 1px solid #d9e6e4;
    border-radius: 16px;
    background:
      linear-gradient(180deg, rgba(255,255,255,.96), rgba(250,253,252,.92));
    display: flex;
    align-items: center;
    padding: 0;
    color: #111827;
    cursor: pointer;
    text-align: left;
    box-shadow: 0 10px 28px rgba(15, 40, 39, .035);
    transition: border-color .16s ease, box-shadow .16s ease, background .16s ease, transform .16s ease;
    font: inherit;
  }

  .profile-select-button:hover {
    border-color: rgba(0,103,95,.28);
    background: white;
  }

  .profile-select-button:focus-visible,
  .profile-select-wrap.is-open .profile-select-button {
    outline: none;
    border-color: rgba(0,103,95,.42);
    box-shadow: 0 0 0 4px rgba(0,103,95,.08), 0 18px 42px rgba(15, 40, 39, .08);
    background: white;
  }

  .profile-select-value {
    min-width: 0;
    flex: 1 1 auto;
    color: #111827;
    font-weight: 850;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 12px;
  }

  .profile-select-value.is-placeholder {
    color: #9aa8b7;
  }

  .profile-select-chevron {
    width: 42px;
    align-self: stretch;
    display: grid;
    place-items: center;
    color: var(--loop-teal);
    border-left: 1px solid #edf3f2;
    flex: 0 0 auto;
    transition: transform .16s ease;
  }

  .profile-select-wrap.is-open .profile-select-chevron {
    transform: rotate(180deg);
  }

  .profile-select-menu {
    position: fixed;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 8px;
    border-radius: 18px;
    border: 1px solid rgba(0,103,95,.16);
    background: rgba(255,255,255,.98);
    box-shadow: 0 24px 70px rgba(15, 40, 39, .16);
    backdrop-filter: blur(18px);
    z-index: 2147483602;
    animation: profileDropdownIn .14s ease both;
  }

  .profile-select-menu.is-up {
    transform-origin: bottom center;
  }

  .profile-select-menu.is-down {
    transform-origin: top center;
  }

  @keyframes profileDropdownIn {
    from { opacity: 0; transform: translateY(4px) scale(.985); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  .profile-select-menu::-webkit-scrollbar {
    width: 10px;
  }

  .profile-select-menu::-webkit-scrollbar-track {
    background: transparent;
  }

  .profile-select-menu::-webkit-scrollbar-thumb {
    background: rgba(0,103,95,.18);
    border: 3px solid rgba(255,255,255,.98);
    border-radius: 999px;
  }

  .profile-select-menu button {
    width: 100%;
    min-height: 40px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 12px;
    color: #162236;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 850;
    text-align: left;
  }

  .profile-select-menu button:hover {
    background: rgba(0,103,95,.07);
    color: var(--loop-teal);
  }

  .profile-select-menu button.is-active {
    background: linear-gradient(135deg, rgba(0,103,95,.12), rgba(0,137,127,.08));
    color: var(--loop-teal);
  }

  .profile-select-menu button:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .profile-select-menu button strong {
    width: 22px;
    height: 22px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(0,103,95,.10);
    color: var(--loop-teal);
    font-size: 12px;
    flex: 0 0 auto;
  }

  .profile-select-sheet-layer {
    position: fixed;
    inset: 0;
    z-index: 2147483603;
    display: grid;
    align-items: end;
    pointer-events: auto;
  }

  .profile-select-sheet-backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(12, 24, 24, .28);
    backdrop-filter: blur(6px);
    cursor: pointer;
  }

  .profile-select-sheet {
    position: relative;
    width: min(100% - 20px, 560px);
    margin: 0 auto 10px;
    max-height: min(74vh, 560px);
    overflow: hidden;
    border-radius: 26px;
    border: 1px solid rgba(0,103,95,.16);
    background: rgba(255,255,255,.98);
    box-shadow: 0 28px 90px rgba(15, 40, 39, .22);
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    animation: profileSheetIn .18s ease both;
  }

  @keyframes profileSheetIn {
    from { opacity: 0; transform: translateY(18px) scale(.985); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  .profile-select-sheet-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 18px 12px;
    border-bottom: 1px solid rgba(219,231,229,.84);
  }

  .profile-select-sheet-head span {
    display: block;
    color: var(--loop-teal);
    font-size: 10px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .16em;
    margin-bottom: 3px;
  }

  .profile-select-sheet-head strong {
    display: block;
    color: #101827;
    font-size: 20px;
    font-weight: 950;
    letter-spacing: -.03em;
  }

  .profile-select-sheet-head > button {
    width: 42px;
    height: 42px;
    border-radius: 999px;
    border: 1px solid #dbe7e5;
    background: white;
    color: #111827;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }

  .profile-select-sheet-options {
    overflow: auto;
    overscroll-behavior: contain;
    padding: 10px;
  }

  .profile-select-sheet-options button {
    width: 100%;
    min-height: 48px;
    border: 0;
    border-radius: 14px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 14px;
    color: #162236;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 900;
    text-align: left;
  }

  .profile-select-sheet-options button:hover {
    background: rgba(0,103,95,.07);
    color: var(--loop-teal);
  }

  .profile-select-sheet-options button.is-active {
    background: linear-gradient(135deg, rgba(0,103,95,.12), rgba(0,137,127,.08));
    color: var(--loop-teal);
  }

  .profile-select-sheet-options button strong {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(0,103,95,.10);
    color: var(--loop-teal);
    font-size: 12px;
    flex: 0 0 auto;
  }

  .profile-input-wrap.is-textarea {
    align-items: flex-start;
    padding-top: 12px;
  }

  .profile-input-wrap textarea {
    min-height: 92px;
    resize: vertical;
    padding-top: 2px;
  }

  .profile-phone-country-button {
    height: 52px;
    border: 0;
    border-right: 1px solid #d9e6e4;
    background: #f6f9f9;
    padding: 0 11px;
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    color: #111827;
    font-weight: 950;
    flex: 0 0 auto;
  }

  .profile-country-badge {
    display: inline-grid;
    place-items: center;
    min-width: 25px;
    height: 22px;
    border-radius: 9px;
    background: white;
    border: 1px solid #d6e4e2;
    color: var(--loop-teal);
    font-size: 10px;
    font-weight: 950;
  }

  .profile-phone-field {
    position: relative;
  }

  .profile-phone-country-menu {
    position: absolute;
    left: 0;
    top: calc(100% + 8px);
    width: min(320px, 92vw);
    background: white;
    border: 1px solid #dbe7e5;
    border-radius: 18px;
    box-shadow: 0 22px 60px rgba(15, 23, 42, .16);
    padding: 8px;
    z-index: 60;
    display: grid;
    gap: 4px;
  }

  .profile-phone-country-menu button {
    border: 0;
    background: transparent;
    border-radius: 12px;
    padding: 10px;
    display: flex;
    gap: 10px;
    text-align: left;
    cursor: pointer;
    align-items: center;
  }

  .profile-phone-country-menu button.active,
  .profile-phone-country-menu button:hover {
    background: rgba(0,103,95,.08);
  }

  .profile-phone-country-menu strong {
    display: block;
    font-size: 13px;
  }

  .profile-phone-country-menu em {
    display: block;
    font-size: 11px;
    color: var(--loop-muted);
    font-style: normal;
    font-weight: 800;
  }

  .profile-v61-details-card {
    overflow: hidden;
  }

  .profile-v61-details-card summary {
    list-style: none;
    cursor: pointer;
    padding: 18px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  .profile-v61-details-card summary::-webkit-details-marker { display: none; }

  .profile-v61-details-card summary span {
    display: block;
    color: var(--loop-teal);
    text-transform: uppercase;
    letter-spacing: .16em;
    font-size: 11px;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .profile-v61-details-card summary strong {
    font-size: 15px;
    letter-spacing: -.02em;
  }

  .profile-v61-details-card[open] summary {
    border-bottom: 1px solid #e6eeee;
  }

  .profile-optional-fields {
    padding: 18px 22px 22px;
  }

  .profile-v61-row-card,
  .profile-preference,
  .profile-v61-setting-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
  }

  .profile-v61-row-card span,
  .profile-v61-setting-row span {
    display: block;
    color: var(--loop-muted);
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: .08em;
    margin-bottom: 5px;
  }

  .profile-v61-row-card strong,
  .profile-v61-setting-row strong {
    display: block;
    font-size: 15px;
    color: #111827;
    font-weight: 950;
  }

  .profile-v61-notification-list {
    display: grid;
    gap: 10px;
  }

  .profile-notification-row {
    min-height: 68px;
    padding: 14px 16px;
    border: 1px solid #e0ebe9;
    border-radius: 18px;
    background: #fff;
  }

  .profile-notification-row strong {
    display: block;
    font-size: 14px;
    margin-bottom: 4px;
  }

  .profile-notification-row span {
    display: block;
    color: var(--loop-muted);
    font-size: 12px;
    line-height: 1.35;
    font-weight: 750;
  }

  .profile-switch {
    width: 52px;
    height: 30px;
    border: 1px solid #d1e0de;
    border-radius: 999px;
    background: #e8eef2;
    padding: 3px;
    cursor: pointer;
    transition: background .16s ease, border-color .16s ease;
    flex: 0 0 auto;
  }

  .profile-switch span {
    display: block;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 2px 8px rgba(15, 23, 42, .18);
    transition: transform .16s ease;
  }

  .profile-switch.is-on {
    background: #18bf8f;
    border-color: #16a77f;
  }

  .profile-switch.is-on span {
    transform: translateX(21px);
  }

  .profile-v61-actions-row {
    margin-top: 16px;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .profile-v61-settings-list {
    display: grid;
    gap: 0;
    padding: 0;
    overflow: hidden;
  }

  .profile-v61-setting-row {
    padding: 18px 20px;
    border-bottom: 1px solid #e6eeee;
  }

  .profile-v61-setting-row:last-child {
    border-bottom: 0;
  }

  .profile-technical-id-details {
    padding: 0;
    border-top: 1px solid #e6eeee;
  }

  .profile-technical-id-details summary {
    cursor: pointer;
    list-style: none;
    padding: 16px 20px;
    color: var(--loop-muted);
    font-weight: 900;
  }

  .profile-technical-id-details summary::-webkit-details-marker { display: none; }

  .profile-technical-id-box {
    padding: 0 20px 18px;
    display: grid;
    gap: 8px;
  }

  .profile-technical-id-box code {
    display: block;
    padding: 12px;
    border-radius: 14px;
    background: #f6f9f9;
    color: #0f172a;
    word-break: break-all;
  }

  .profile-technical-id-box button {
    justify-self: start;
    border: 1px solid #d6e4e2;
    background: white;
    border-radius: 999px;
    padding: 8px 12px;
    font-weight: 950;
    cursor: pointer;
    color: var(--loop-teal);
  }

  .profile-danger-zone {
    padding: 20px;
    border-color: rgba(239, 68, 68, .20);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    box-shadow: 0 24px 70px rgba(127, 29, 29, .05);
  }

  .profile-danger-zone h3 {
    margin: 6px 0 6px;
    font-size: 20px;
    letter-spacing: -.04em;
  }

  .profile-danger-zone p {
    margin: 0;
    color: var(--loop-muted);
    font-size: 13px;
    font-weight: 750;
  }

  .profile-danger-kicker {
    color: #b91c1c;
  }

  .profile-secondary-btn,
  .profile-save-btn,
  .profile-outline-action,
  .profile-delete-link-btn,
  .profile-keep-account-btn,
  .profile-confirm-delete-btn {
    border-radius: 999px;
    min-height: 42px;
    padding: 0 18px;
    font-weight: 950;
    cursor: pointer;
    border: 1px solid #d7e5e3;
    background: white;
    color: #111827;
    font-family: inherit;
  }

  .profile-save-btn {
    background: var(--loop-teal);
    color: white;
    border-color: var(--loop-teal);
    box-shadow: 0 15px 35px rgba(0, 103, 95, .18);
  }

  .profile-save-btn.is-saved,
  .profile-save-btn:disabled {
    background: #eef2f3;
    border-color: #eef2f3;
    color: #9aa8b7;
    box-shadow: none;
    cursor: default;
  }

  .profile-save-btn.small,
  .profile-secondary-btn.mini {
    min-height: 36px;
    padding: 0 14px;
    font-size: 12px;
  }

  .profile-outline-action {
    color: var(--loop-teal);
  }

  .profile-delete-link-btn.stronger {
    color: #dc2626;
    border-color: rgba(239, 68, 68, .22);
    background: #fffafa;
  }

  .profile-mobile-tabs {
    display: none;
  }

  .profile-toast {
    position: fixed;
    top: 82px;
    right: 24px;
    z-index: 2147483600;
    max-width: min(420px, calc(100vw - 32px));
    border-radius: 18px;
    padding: 13px 16px;
    font-weight: 900;
    box-shadow: 0 18px 50px rgba(15, 23, 42, .18);
    background: white;
    border: 1px solid #dbe7e5;
  }

  .profile-toast.is-success {
    border-color: rgba(0, 103, 95, .22);
    color: var(--loop-teal);
  }

  .profile-toast.is-error {
    border-color: rgba(239, 68, 68, .30);
    color: #b91c1c;
  }

  .profile-empty {
    width: min(520px, calc(100vw - 32px));
    margin: 70px auto;
    padding: 34px;
    background: white;
    border: 1px solid var(--loop-line);
    border-radius: 28px;
    text-align: center;
    box-shadow: var(--loop-shadow);
  }

  .profile-empty-icon {
    width: 58px;
    height: 58px;
    margin: 0 auto 16px;
    border-radius: 20px;
    display: grid;
    place-items: center;
    background: rgba(0,103,95,.10);
    color: var(--loop-teal);
    font-weight: 950;
  }

  .profile-skeleton {
    border-radius: 24px;
    background: linear-gradient(90deg, #edf5f4, #fff, #edf5f4);
    background-size: 240% 100%;
    animation: profileShimmer 1.2s infinite linear;
  }

  .profile-skeleton.hero { height: 170px; }
  .profile-skeleton.card { height: 220px; }
  .profile-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }

  @keyframes profileShimmer { to { background-position: -240% 0; } }

  .profile-delete-modal {
    position: fixed;
    inset: 0;
    z-index: 2147483650;
    display: grid;
    place-items: center;
    padding: 24px;
  }

  .profile-delete-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, .48);
    border: 0;
  }

  .profile-delete-panel {
    position: relative;
    width: min(540px, 100%);
    background: white;
    border-radius: 28px;
    padding: 24px;
    border: 1px solid #e6eeee;
    box-shadow: 0 34px 90px rgba(15, 23, 42, .30);
  }

  .profile-delete-panel-head {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .profile-delete-shield-icon {
    width: 52px;
    height: 52px;
    display: grid;
    place-items: center;
    border-radius: 18px;
    background: #fef2f2;
    color: #dc2626;
  }

  .profile-delete-panel h2 {
    margin: 4px 0 0;
    font-size: 28px;
    letter-spacing: -.055em;
  }

  .profile-delete-summary,
  .profile-delete-social-note {
    color: var(--loop-muted);
    font-size: 14px;
    line-height: 1.45;
    font-weight: 750;
  }

  .profile-delete-info-box {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 16px 0;
  }

  .profile-delete-info-box div {
    padding: 12px;
    border: 1px solid #e6eeee;
    border-radius: 16px;
    background: #fbfdfd;
  }

  .profile-delete-info-box span,
  .profile-delete-password-field span {
    display: block;
    color: var(--loop-muted);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .08em;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .profile-delete-info-box strong {
    font-size: 13px;
    word-break: break-word;
  }

  .profile-delete-password-field {
    display: grid;
    gap: 8px;
  }

  .profile-delete-password-field input {
    min-height: 48px;
    border: 1px solid #dbe7e5;
    border-radius: 15px;
    padding: 0 14px;
    font: inherit;
    font-weight: 850;
  }

  .profile-delete-ack {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin: 16px 0;
    color: #374151;
    font-size: 13px;
    font-weight: 800;
  }

  .profile-delete-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  .profile-confirm-delete-btn {
    background: #dc2626;
    color: white;
    border-color: #dc2626;
  }

  @media (max-width: 1100px) {
    .profile-v61-shell {
      grid-template-columns: 246px minmax(0, 1fr);
      padding: 26px 18px 60px;
      gap: 18px;
    }

    .profile-v61-header h1 {
      font-size: 46px;
    }
  }

  @media (max-width: 860px) {
    .profile-v61-shell {
      display: block;
      padding: 18px 12px 50px;
    }

    .profile-v61-sidebar {
      position: relative;
      top: auto;
      margin-bottom: 14px;
    }

    .profile-v61-nav {
      display: none;
    }

    .profile-v61-account {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      gap: 12px;
      align-items: center;
      padding: 14px;
      border-radius: 22px;
    }

    .profile-v61-account-copy {
      margin-top: 0;
    }

    .profile-v61-account-meta,
    .profile-v61-progress,
    .profile-v61-sidebar-actions {
      grid-column: 1 / -1;
      margin-top: 0;
    }

    .profile-mobile-tabs {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding: 4px 0 8px;
      scrollbar-width: none;
    }

    .profile-mobile-tabs::-webkit-scrollbar { display: none; }

    .profile-mobile-tabs button {
      border: 1px solid #dbe7e5;
      background: white;
      color: #334155;
      border-radius: 999px;
      padding: 9px 13px;
      font-weight: 950;
      white-space: nowrap;
      cursor: pointer;
    }

    .profile-mobile-tabs button.is-active {
      background: var(--loop-teal);
      border-color: var(--loop-teal);
      color: white;
    }

    .profile-v61-header {
      align-items: flex-start;
      padding-top: 10px;
    }

    .profile-v61-header h1 {
      font-size: 40px;
    }

    .profile-v61-header-actions {
      align-self: flex-start;
    }

    .profile-v61-identity,
    .profile-v61-section-title,
    .profile-v61-row-card,
    .profile-v61-setting-row,
    .profile-danger-zone {
      grid-template-columns: 1fr;
      display: grid;
      align-items: start;
    }

    .profile-v61-status-chip {
      justify-self: start;
    }

    .profile-fields-grid,
    .profile-delete-info-box {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 560px) {
    .profile-page {
      min-height: 100vh;
    }

    .profile-v61-header {
      display: grid;
      gap: 12px;
    }

    .profile-v61-header h1 {
      font-size: 34px;
    }

    .profile-v61-card,
    .profile-v61-details-card,
    .profile-danger-zone {
      border-radius: 20px;
    }

    .profile-v61-card {
      padding: 16px;
    }

    .profile-v61-section-title {
      display: grid;
      gap: 8px;
      align-items: start;
    }

    .profile-v61-actions-row,
    .profile-delete-actions {
      justify-content: stretch;
    }

    .profile-v61-actions-row button,
    .profile-delete-actions button,
    .profile-save-btn,
    .profile-secondary-btn,
    .profile-outline-action {
      width: 100%;
    }

    .profile-toast {
      top: 72px;
      left: 12px;
      right: 12px;
    }
  }

  /* v62 — Profile settings layout cleanup */
  .profile-v62 .profile-v61-shell {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 18px clamp(22px, 4vw, 68px) 72px 0;
    grid-template-columns: 292px minmax(0, 980px);
    gap: clamp(24px, 3.5vw, 48px);
    justify-content: start;
  }

  .profile-v62 .profile-v61-sidebar {
    top: 84px;
    padding-left: 22px;
    gap: 14px;
  }

  .profile-v62-sidebar-title,
  .profile-v62 .profile-v61-nav {
    background: rgba(255,255,255,.84);
    border: 1px solid var(--loop-line);
    border-radius: 22px;
    box-shadow: 0 18px 46px rgba(15, 40, 39, .065);
    backdrop-filter: blur(14px);
  }

  .profile-v62-sidebar-title {
    padding: 16px;
  }

  .profile-v62-sidebar-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
  }

  .profile-v62-sidebar-title-row strong {
    display: block;
    font-size: 15px;
    font-weight: 950;
    letter-spacing: -.03em;
    color: #0f172a;
  }

  .profile-v62-sidebar-title-row span {
    display: block;
    margin-top: 2px;
    font-size: 11.5px;
    font-weight: 850;
    color: var(--loop-muted);
  }

  .profile-v62 .profile-v61-nav {
    padding: 8px;
  }

  .profile-v62 .profile-v61-nav button {
    border-radius: 15px;
    padding: 14px 14px;
  }

  .profile-v62 .profile-v61-nav button.is-active {
    background: linear-gradient(135deg, rgba(0,103,95,.12), rgba(0,103,95,.045));
    box-shadow: inset 3px 0 0 var(--loop-teal);
  }

  .profile-v62 .profile-v61-main {
    gap: 14px;
  }

  .profile-v62 .profile-v61-header {
    padding: 8px 0 8px;
    min-height: 108px;
    align-items: center;
  }

  .profile-v62 .profile-v61-header h1 {
    font-size: clamp(58px, 6vw, 78px);
    line-height: .88;
    margin: 8px 0 8px;
  }

  .profile-v62 .profile-v61-header p {
    max-width: 580px;
  }

  .profile-v62 .profile-v61-header-actions {
    align-self: center;
    min-width: 180px;
  }

  .profile-v62 .profile-save-btn {
    min-width: 132px;
    justify-content: center;
  }

  .profile-v62 .profile-save-state,
  .profile-v62 .profile-v61-identity {
    display: none !important;
  }

  .profile-v62 .profile-v61-card,
  .profile-v62 .profile-v61-details-card,
  .profile-v62 .profile-danger-zone {
    border-radius: 24px;
    box-shadow: 0 20px 54px rgba(15, 40, 39, .055);
    background: rgba(255,255,255,.9);
  }

  .profile-v62 .profile-v61-card {
    padding: 22px 24px;
  }

  .profile-v62 .profile-v61-memberships-card {
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
    overflow: visible;
  }

  .profile-v62-profile-card {
    margin-top: 0;
  }

  .profile-v62-section-title-clean {
    align-items: center;
    padding-bottom: 16px;
    margin-bottom: 16px;
  }

  .profile-v62-section-title-clean h2 {
    font-size: 25px;
    font-weight: 860;
    letter-spacing: -.055em;
  }

  .profile-v62-section-title-clean p {
    max-width: 430px;
    text-align: right;
  }

  .profile-v62-inline-divider {
    height: 1px;
    background: #e5eeee;
    margin: 18px -2px 16px;
  }

  .profile-v62-subsection-head {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 16px;
    margin-bottom: 12px;
  }

  .profile-v62-subsection-head span {
    color: var(--loop-teal);
    text-transform: uppercase;
    letter-spacing: .16em;
    font-size: 11px;
    font-weight: 950;
  }

  .profile-v62-subsection-head p {
    margin: 0;
    color: var(--loop-muted);
    font-size: 12px;
    font-weight: 800;
  }

  .profile-v62-optional-inline {
    padding: 0 !important;
  }

  .profile-v62 .profile-phone-country-button strong {
    white-space: nowrap;
  }

  .profile-v62 .profile-country-badge {
    min-width: 28px;
    height: 22px;
    font-size: 15px;
    background: transparent;
    border: 0;
    border-radius: 0;
  }

  .profile-v62 .profile-phone-country-button {
    min-width: 102px;
    justify-content: center;
    background: #f8fbfa;
    padding: 0 12px;
  }

  .profile-v62 .profile-phone-chevron {
    color: #8ca0b3;
    font-size: 11px;
  }

  .profile-v62 .profile-input-wrap,
  .profile-v62 .profile-phone-combo {
    min-height: 50px;
    border-radius: 15px;
    background: rgba(255,255,255,.95);
  }

  .profile-v62 .profile-fields-grid {
    gap: 14px 16px;
  }

  .profile-v62 .profile-mobile-tabs {
    display: none;
  }

  .profile-v62 .profile-v61-actions-row {
    justify-content: flex-start;
  }

  @media (max-width: 1100px) {
    .profile-v62 .profile-v61-shell {
      grid-template-columns: 1fr;
      padding: 20px 18px 68px;
      gap: 16px;
    }

    .profile-v62 .profile-v61-sidebar {
      position: relative;
      top: auto;
      padding-left: 0;
    }

    .profile-v62-sidebar-title {
      display: none;
    }

    .profile-v62 .profile-v61-nav {
      display: flex;
      overflow-x: auto;
      gap: 8px;
      border-radius: 18px;
    }

    .profile-v62 .profile-v61-nav button {
      min-width: 136px;
    }

    .profile-v62 .profile-v61-header {
      min-height: 0;
      padding-top: 4px;
    }

    .profile-v62 .profile-v61-header h1 {
      font-size: clamp(44px, 10vw, 58px);
    }
  }

  @media (max-width: 760px) {
    .profile-v62 .profile-v61-shell {
      padding: 14px 12px 72px;
    }

    .profile-v62 .profile-v61-header {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }

    .profile-v62 .profile-v61-header-actions {
      width: 100%;
      justify-content: stretch;
    }

    .profile-v62 .profile-save-btn {
      width: 100%;
    }

    .profile-v62 .profile-fields-grid {
      grid-template-columns: 1fr;
    }

    .profile-v62-section-title-clean,
    .profile-v62-subsection-head {
      display: grid;
      align-items: start;
    }

    .profile-v62-section-title-clean p {
      text-align: left;
    }

    .profile-v62 .profile-phone-country-button {
      min-width: 96px;
    }
  }



  /* v63 — Fit horizontal space + remove address/postal from Profile UI */
  .profile-v62 .profile-v61-shell {
    width: 100%;
    max-width: none;
    grid-template-columns: 292px minmax(0, 1fr);
    gap: clamp(28px, 3vw, 56px);
    padding-right: clamp(24px, 3.5vw, 56px);
  }

  .profile-v62 .profile-v61-main,
  .profile-v62 .profile-v61-content,
  .profile-v62 .profile-v61-card {
    width: 100%;
    max-width: none;
  }

  .profile-v62 .profile-v62-profile-card {
    max-width: 1240px;
  }

  @media (min-width: 1540px) {
    .profile-v62 .profile-v61-shell {
      grid-template-columns: 292px minmax(0, 1fr);
      padding-right: clamp(40px, 4vw, 82px);
    }

    .profile-v62 .profile-v62-profile-card {
      max-width: 1320px;
    }
  }

  @media (max-width: 1100px) {
    .profile-v62 .profile-v61-shell {
      grid-template-columns: 1fr;
      padding-right: 18px;
    }

    .profile-v62 .profile-v62-profile-card {
      max-width: none;
    }
  }


  /* v66 — Profile phone uses the same premium PhoneKit as WorkersSection */
  .profile-v62 .profile-phonekit-field {
    min-width: 0;
  }

  .profile-v62 .profile-phonekit-field .loop-phone-input {
    width: 100%;
    min-width: 0;
  }

  .profile-v62 .profile-phonekit-field .loop-phone-label {
    display: block;
    margin: 0 0 8px;
    color: #334155;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: 0;
  }

  .profile-v62 .profile-phonekit-field .loop-phone-control,
  .profile-v62 .profile-phonekit-field .workers-phone-premium-input {
    min-height: 50px !important;
    border-radius: 15px !important;
    border: 1px solid #d9e6e4 !important;
    background: rgba(255,255,255,.95) !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .profile-v62 .profile-phonekit-field .loop-phone-control:focus-within,
  .profile-v62 .profile-phonekit-field .workers-phone-premium-input:focus-within {
    border-color: rgba(0,103,95,.42) !important;
    box-shadow: 0 0 0 4px rgba(0,103,95,.08) !important;
    background: #fff !important;
  }

  .profile-v62 .profile-phonekit-field .loop-phone-hint,
  .profile-v62 .profile-phonekit-field em {
    display: none !important;
  }

  .profile-v62 .profile-phonekit-field input {
    font-weight: 850 !important;
    color: #111827 !important;
  }

  .profile-v62 .profile-phonekit-field button {
    font-weight: 950;
  }

  @media (max-width: 680px) {
    .profile-v62 .profile-phonekit-field .loop-phone-control,
    .profile-v62 .profile-phonekit-field .workers-phone-premium-input {
      min-height: 48px !important;
    }
  }


  /* v68 — Preferencias unificadas */
  .profile-v68-preferences-card {
    margin-bottom: 18px;
  }

  .profile-v68-notification-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .profile-v68-notification-list .profile-preference.clean {
    min-height: 76px;
  }

  .profile-v68-privacy-card {
    margin-top: 2px;
  }

  @media (max-width: 860px) {
    .profile-v68-notification-list {
      grid-template-columns: 1fr;
    }
  }


  /* v71 — Mobile header gap safe fix
     Do NOT hide .mainmenu-header-only-root: on some breakpoints it carries the visible mobile header.
     Instead, pull ProfilePage up by the extra 58px spacer that appears between the fixed header and the page. */
  @media (max-width: 860px) {
    body:has(.profile-page.profile-v61:not(.account-refresh)) .mainmenu-header-only-root {
      display: block !important;
      height: 58px !important;
      min-height: 58px !important;
      overflow: visible !important;
      pointer-events: auto !important;
    }

    body:has(.profile-page.profile-v61:not(.account-refresh)) .profile-page {
      margin-top: -58px !important;
      min-height: calc(100vh - 58px);
      position: relative;
      z-index: 1;
    }

    .profile-v61.profile-v62 .profile-v61-shell,
    .profile-v61 .profile-v61-shell {
      padding-top: 8px !important;
    }

    .profile-v61 .profile-v61-sidebar {
      margin-top: 0 !important;
    }
  }


  /* R32 — Firebase SMS phone verification */
  .profile-phone-verification {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #d9e6e4;
    border-radius: 18px;
    background: #f8fbfb;
  }

  .profile-phone-verification.is-verified {
    border-color: rgba(0,103,95,.24);
    background: #effaf7;
  }

  .profile-phone-verification.is-code {
    align-items: start;
    border-color: rgba(0,103,95,.28);
    background: #f4fbfa;
  }

  .profile-phone-verification-copy {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
  }

  .profile-phone-verification-icon {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba(0,103,95,.10);
    color: #00675f;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: .02em;
  }

  .profile-phone-verification.is-verified .profile-phone-verification-icon {
    background: #00675f;
    color: #fff;
    font-size: 18px;
  }

  .profile-phone-verification-copy strong {
    display: block;
    margin: 1px 0 4px;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .profile-phone-verification-copy p {
    margin: 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.45;
  }

  .profile-phone-verification-actions {
    display: flex;
    align-items: end;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
  }

  .profile-phone-primary-action,
  .profile-phone-secondary-action {
    min-height: 42px;
    border-radius: 13px;
    padding: 0 15px;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
  }

  .profile-phone-primary-action {
    border: 1px solid #00675f;
    background: #00675f;
    color: #fff;
  }

  .profile-phone-secondary-action {
    border: 1px solid #cbdad8;
    background: #fff;
    color: #00675f;
  }

  .profile-phone-primary-action:disabled,
  .profile-phone-secondary-action:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .profile-phone-code-field {
    display: grid;
    gap: 5px;
  }

  .profile-phone-code-field span {
    color: #64748b;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: .04em;
  }

  .profile-phone-code-field input {
    width: 124px;
    min-height: 42px;
    border: 1px solid #cbdad8;
    border-radius: 13px;
    outline: none;
    background: #fff;
    color: #0f172a;
    padding: 0 12px;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: .14em;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .profile-phone-code-field input:focus {
    border-color: rgba(0,103,95,.54);
    box-shadow: 0 0 0 4px rgba(0,103,95,.08);
  }

  .profile-phone-status {
    grid-column: 1 / -1;
    padding: 10px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 850;
  }

  .profile-phone-status.is-success {
    background: rgba(0,103,95,.08);
    color: #00675f;
  }

  .profile-phone-status.is-info {
    background: rgba(14,116,144,.08);
    color: #0e7490;
  }

  .profile-phone-status.is-error {
    background: #fff1f2;
    color: #b42318;
  }

  .profile-phone-recaptcha {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    pointer-events: none;
    opacity: 0;
  }

  @media (max-width: 820px) {
    .profile-phone-verification {
      grid-template-columns: 1fr;
    }

    .profile-phone-verification-actions {
      justify-content: flex-start;
    }
  }

  @media (max-width: 520px) {
    .profile-phone-verification-actions {
      display: grid;
      grid-template-columns: 1fr;
      align-items: stretch;
    }

    .profile-phone-primary-action,
    .profile-phone-secondary-action,
    .profile-phone-code-field,
    .profile-phone-code-field input {
      width: 100%;
    }
  }

`;



